import { db, auth, storage, firebaseConfig } from '../firebase';
import { collection, addDoc, serverTimestamp, query, where, getDocs, orderBy, limit, deleteDoc, doc, arrayUnion, updateDoc, getDoc, setDoc, increment } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getCache, setCache, createCacheKey, clearCache } from './cache';
import { artStyles } from '../data/artStyles';

// Activity types
export const ACTIVITY_TYPES = {
  APPRECIATION: 'appreciation',
  STORY: 'story',
  REIMAGINE: 'reimagine',
  TUTORIAL: 'tutorial',
  ART_HISTORY_APPRECIATION: 'art-history-appreciation',
  ART_COMPARISON: 'art-comparison'
};

/**
 * Stable identity hash for an artwork URL or path (same rolling hash as saveActivity / checkExistingActivity).
 * Prefer matching on metadata.imageHash over raw originalImageUrl when URLs can differ (CDN, localhost, tokens).
 */
export const computeImageIdentityHash = (urlOrPath) => {
  if (!urlOrPath || typeof urlOrPath !== 'string' || urlOrPath.length === 0) return null;
  try {
    let h = 0;
    for (let i = 0; i < urlOrPath.length; i++) {
      h = (h * 31 + urlOrPath.charCodeAt(i)) >>> 0;
    }
    return h.toString(16);
  } catch (_) {
    return null;
  }
};

/** One artwork + one style = one Ways of Seeing journey (stable composite for queries). */
export const computeWaysOfSeeingJourneyKey = (originalImageUrl, styleId) => {
  const h = computeImageIdentityHash(originalImageUrl);
  if (!h || styleId == null || String(styleId).trim() === '') return null;
  return `${h}_${String(styleId).trim()}`;
};

/** Same artwork + same style → one row (newest updatedAt) for dashboard scrollers. */
export function dedupeWaysOfSeeingActivitiesByArtworkStyle(activities = []) {
  if (!Array.isArray(activities) || activities.length === 0) return [];

  const timeMs = (act) => {
    const u =
      act.updatedAt?.toMillis?.() ??
      (act.updatedAt?.seconds != null ? act.updatedAt.seconds * 1000 : null);
    const c =
      act.createdAt?.toMillis?.() ??
      (act.createdAt?.seconds != null ? act.createdAt.seconds * 1000 : null);
    return Math.max(u || 0, c || 0);
  };

  const byKey = {};
  for (const act of activities) {
    const styleId = act.styleInfo?.id || act.metadata?.wosStyleId || '';
    const key =
      act.metadata?.wosJourneyKey ||
      (act.metadata?.imageHash && styleId
        ? `${act.metadata.imageHash}::${styleId}`
        : styleId
          ? `${String(act.originalImageUrl || '')}::${styleId}`
          : act.id);

    const prev = byKey[key];
    if (!prev || timeMs(act) >= timeMs(prev)) {
      byKey[key] = act;
    }
  }

  return Object.values(byKey);
}

/**
 * Remove undefined values from an object (Firestore doesn't allow undefined)
 * @param {Object} obj - Object to clean
 * @returns {Object} - Object with undefined values removed
 */
const removeUndefined = (obj) => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefined(item)).filter(item => item !== undefined);
  }
  
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = removeUndefined(value);
    }
  }
  return cleaned;
};

const ENV = (() => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname || '';
    if (host.includes('staging')) {
      return 'staging';
    }
  }
  if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_ENV) {
    return process.env.REACT_APP_ENV;
  }
  return 'prod';
})();

/**
 * Save an activity to Firestore
 * @param {Object} activityData - The activity data to save
 * @param {string} activityData.type - Type of activity (appreciation, story, reimagine, tutorial)
 * @param {string} activityData.title - Activity title
 * @param {string} activityData.description - Activity description
 * @param {string} activityData.originalImageUrl - Original artwork URL
 * @param {string} activityData.transformedImageUrl - Transformed artwork URL (optional)
 * @param {Object} activityData.styleInfo - Style information (optional)
 * @param {Array} activityData.aiPoints - Artmosphere insights points (optional)
 * @param {Object} activityData.tutorial - Tutorial data (optional)
 * @param {Object} activityData.metadata - Additional metadata (optional)
 */
export const saveActivity = async (activityData) => {
  const user = auth.currentUser;
  console.log('🔐 User authentication check:', {
    user: user ? { uid: user.uid, email: user.email } : null,
    isAuthenticated: !!user
  });
  
  if (!user) {
    console.warn('No user logged in, cannot save activity');
    return null;
  }

  try {
    const activity = removeUndefined({
      userId: user.uid,
      type: activityData.type,
      title: activityData.title || '',
      description: activityData.description || '',
      originalImageUrl: activityData.originalImageUrl || '',
      transformedImageUrl: activityData.transformedImageUrl || '',
      styleInfo: activityData.styleInfo || null,
      aiPoints: activityData.aiPoints || [],
      // Enriched fields for richer detail views
      suggestion: activityData.suggestion || '',
      techniques: activityData.techniques || [],
      artAnalysis: activityData.artAnalysis || null,
      concepts: activityData.concepts || [],
      tutorial: activityData.tutorial || null,
      metadata: activityData.metadata || {},
      environment: ENV,
      projectId: firebaseConfig?.projectId || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    console.log('📝 Attempting to save activity:', {
      userId: activity.userId,
      type: activity.type,
      title: activity.title,
      hasAiPoints: !!activity.aiPoints.length
    });

    const docRef = await addDoc(collection(db, 'userActivities'), activity);
    console.log('✅ Activity saved successfully:', docRef.id);
    
    // Clear cache to force refresh (user's own activities)
    const cacheKey = createCacheKey('userActivities', { userId: user.uid }, user.uid);
    clearCache(cacheKey);
    console.log('✅ [ACTIVITY_SERVICE] Cache cleared after saveActivity');
    
    return docRef.id;
  } catch (error) {
    console.error('❌ Error saving activity:', error);
    console.error('❌ Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    
    // If it's a permission error, try to provide helpful information
    if (error.code === 'permission-denied') {
      console.error('🔍 Permission denied - this might be due to:');
      console.error('   1. Firestore rules not properly deployed');
      console.error('   2. User not properly authenticated');
      console.error('   3. Collection not existing yet');
      console.error('   4. Incorrect userId in activity data');
    }
    
    return null;
  }
};

/**
 * Save Art Appreciation activity
 * @param {Object} params - Appreciation parameters
 */
export const saveAppreciationActivity = async (params) => {
  const {
    originalImageUrl,
    aiPoints,
    suggestion,
    techniques,
    artAnalysis,
    concepts = [],
    title = 'Art Appreciation',
    description = 'Artmosphere analysis of artwork',
    metadata = {}
  } = params;

  // Deduplicate: update existing appreciation for this artwork if present
  try {
    // Always generate imageHash for consistent deduplication
    let imageHash = null;
    try {
      if (typeof originalImageUrl === 'string' && originalImageUrl.length > 0) {
        let h = 0;
        for (let i = 0; i < originalImageUrl.length; i++) {
          h = (h * 31 + originalImageUrl.charCodeAt(i)) >>> 0;
        }
        imageHash = h.toString(16);
        console.log('💾 Generated imageHash for saving:', imageHash, 'URL length:', originalImageUrl.length);
      }
    } catch (e) {
      console.warn('⚠️ Failed to generate imageHash for saving:', e?.message || e);
    }

    const existing = await checkExistingActivity(originalImageUrl, ACTIVITY_TYPES.APPRECIATION);
    if (existing) {
      try {
        await updateDoc(doc(db, 'userActivities', existing.id), {
          aiPoints: aiPoints || existing.aiPoints || [],
          suggestion: suggestion ?? existing.suggestion ?? '',
          techniques: techniques || existing.techniques || [],
          artAnalysis: artAnalysis || existing.artAnalysis || null,
          concepts: concepts || existing.concepts || [],
          metadata: { ...(existing.metadata || {}), ...metadata, imageHash: imageHash || existing.metadata?.imageHash || null, feature: 'art-appreciation', pointsCount: aiPoints?.length || existing.metadata?.pointsCount || 0 },
          title: title || existing.title || 'Art Appreciation',
          description: description || existing.description || 'Artmosphere analysis of artwork',
          updatedAt: serverTimestamp()
        });
        
        // Clear cache to force refresh
        const user = auth.currentUser;
        if (user) {
          const cacheKey = createCacheKey('userActivities', { userId: user.uid }, user.uid);
          clearCache(cacheKey);
          console.log('✅ [ACTIVITY_SERVICE] Cache cleared after appreciation update');
        }
        
        return existing.id;
      } catch (e) {
        console.warn('⚠️ Failed to update existing appreciation activity, will create new one:', e?.message || e);
      }
    }
  } catch (e) {
    console.warn('⚠️ Dedup check failed, proceeding to create activity:', e?.message || e);
  }

  // Compute hash for create path as well
  let createHash = null;
  try {
    if (typeof originalImageUrl === 'string' && originalImageUrl.length > 300) {
      let h = 0;
      for (let i = 0; i < originalImageUrl.length; i++) {
        h = (h * 31 + originalImageUrl.charCodeAt(i)) >>> 0;
      }
      createHash = h.toString(16);
    }
  } catch (_) {}

  const activityDocId = await saveActivity({
    type: ACTIVITY_TYPES.APPRECIATION,
    title,
    description,
    originalImageUrl,
    aiPoints,
    suggestion,
    techniques,
    artAnalysis,
    concepts,
    metadata: {
      feature: 'art-appreciation',
      pointsCount: aiPoints?.length || 0,
      imageHash: createHash || null,
      ...metadata
    }
  });
  
  try {
    // If this is a mentor activity and has orgId, update org stats aggregation
    if (metadata?.context === 'mentor' && metadata?.orgId) {
      const orgId = metadata.orgId;
      const statsRef = doc(db, 'orgActivityStats', orgId);
      const update = {
        updatedAt: serverTimestamp(),
        totalCompleted: increment(1),
        [`features.art-appreciation`]: increment(1)
      };
      await setDoc(statsRef, update, { merge: true });
    }
  } catch (e) {
    console.warn('⚠️ Failed to update org activity stats:', e?.message || e);
  }
  
  return activityDocId;
};

/**
 * Save Art Story activity - Only when explicitly published/saved
 * @param {Object} params - Story parameters
 * @param {boolean} isPublished - Whether the story is being published
 */
export const saveStoryActivity = async (params, isPublished = false) => {
  const {
    originalImageUrl,
    storyTitle,
    storyDescription,
    characters = [],
    scenes = [],
    storyId,
    status = 'draft'
  } = params;

  // Only save to dashboard if story is published or explicitly saved
  if (!isPublished && status !== 'published') {
    console.log('📝 Story not published/saved yet, skipping dashboard save');
    return null;
  }

  return await saveActivity({
    type: ACTIVITY_TYPES.STORY,
    title: storyTitle || 'Art Story Created',
    description: storyDescription || 'Story created from artwork',
    originalImageUrl,
    metadata: {
      feature: 'art-story',
      charactersCount: characters.length,
      scenesCount: scenes.length,
      storyId: storyId,
      status: status,
      isPublished: isPublished
    }
  });
};

/**
 * Save Reimagine activity - Only when explicitly saved
 * @param {Object} params - Reimagine parameters
 * @param {boolean} isSaved - Whether the transformation is being saved
 */
export const saveReimagineActivity = async (params, isSaved = false) => {
  const {
    originalImageUrl,
    transformedImageUrl,
    styleInfo,
    title = 'Artwork Reimagined',
    description = 'Artwork transformed with Artmosphere',
    metadata = {}
  } = params;

  // Only save to dashboard if explicitly saved
  if (!isSaved) {
    console.log('📝 Reimagine not saved yet, skipping dashboard save');
    return null;
  }

  console.log('🔄 saveReimagineActivity called with metadata:', metadata);

  // Support an explicit activity ID to update (used by Reimagine Studio
  // to GUARANTEE that feedback updates the same card that was just
  // auto‑saved). We strip this out of metadata so it is NOT stored.
  const { activityIdForUpdate, ...metadataWithoutId } = metadata || {};

  // If transformedImageUrl is a base64 data URL, upload it to Firebase Storage first
  // to avoid exceeding Firestore's 1MB document size limit
  let finalTransformedImageUrl = transformedImageUrl;
  
  if (transformedImageUrl && typeof transformedImageUrl === 'string' && transformedImageUrl.startsWith('data:image/')) {
    try {
      const base64SizeKB = Math.round(transformedImageUrl.length / 1024);
      console.log('📤 Uploading base64 image to Firebase Storage (size:', base64SizeKB, 'KB)');
      
      // Add timeout to prevent hanging (15 seconds - should be enough for most uploads)
      const uploadTimeout = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Upload timeout after 15 seconds')), 15000)
      );
      
      // Convert data URL to blob
      const response = await fetch(transformedImageUrl);
      const blob = await response.blob();
      console.log('📦 Blob created, size:', Math.round(blob.size / 1024), 'KB');
      
      // Generate a unique filename
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 15);
      const fileExtension = transformedImageUrl.match(/data:image\/(\w+);/)?.[1] || 'jpg';
      const fileName = `reimagine_${timestamp}_${randomId}.${fileExtension}`;
      const storagePath = `user-submissions/${auth.currentUser?.uid || 'anonymous'}/${fileName}`;
      
      // Upload to Firebase Storage with timeout
      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytes(storageRef, blob)
        .then(() => {
          console.log('📤 Upload bytes completed, getting download URL...');
          return getDownloadURL(storageRef);
        })
        .then((url) => {
          console.log('📥 Download URL retrieved:', url);
          return url;
        });
      
      // Wait for upload to complete with timeout
      try {
        finalTransformedImageUrl = await Promise.race([uploadTask, uploadTimeout]);
        console.log('✅ Image uploaded to Firebase Storage:', finalTransformedImageUrl);
      } catch (raceError) {
        // Check if it's a timeout or actual upload error
        if (raceError.message?.includes('timeout')) {
          throw new Error('Upload timeout after 15 seconds');
        }
        throw raceError;
      }
    } catch (uploadError) {
      console.error('❌ Failed to upload image to Firebase Storage:', uploadError);
      console.error('❌ Upload error details:', {
        code: uploadError?.code,
        message: uploadError?.message,
        name: uploadError?.name,
        stack: uploadError?.stack
      });
      // If upload fails, skip saving the activity to avoid size limit error
      // But don't throw - just log and return null so the UI isn't blocked
      console.warn('⚠️ Skipping activity save due to upload failure (would exceed Firestore size limit)');
      return null;
    }
  }

  // Generate a short imageHash for grouping/queries
  let imageHash = null;
  try {
    if (typeof originalImageUrl === 'string' && originalImageUrl.length > 0) {
      let h = 0;
      for (let i = 0; i < originalImageUrl.length; i++) {
        h = (h * 31 + originalImageUrl.charCodeAt(i)) >>> 0;
      }
      imageHash = h.toString(16);
    }
  } catch (_) {}

  // Normalize artmosphereFeedback shape if present (map legacy {label, appreciation} → {title, text})
  let normalizedFeedback = undefined;
  if (Array.isArray(metadataWithoutId?.artmosphereFeedback)) {
    normalizedFeedback = metadataWithoutId.artmosphereFeedback.map(item => ({
      title: item.title || item.label || undefined,
      text: item.text || item.appreciation || undefined,
      suggestion: item.suggestion || undefined
    }));
  }

  const finalMetadata = {
    feature: metadataWithoutId.feature || 'reimagine-artwork',
    styleName: styleInfo?.name || 'Unknown',
    imageHash: metadataWithoutId.imageHash || imageHash || undefined,
    ...(normalizedFeedback ? { artmosphereFeedback: normalizedFeedback } : {}),
    ...metadataWithoutId // Preserve all custom metadata passed from the component (normalized feedback overwritten above)
  };

  // Add mentor-specific metadata if available
  const user = auth.currentUser;
  if (user && user.uid) {
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.accountType === 'mentor' || userData.role === 'mentor') {
          finalMetadata.context = 'mentor';
          finalMetadata.mentorId = user.uid;
          // Ensure no undefined values for Firestore
          finalMetadata.className = metadata.className || userData.lastSelectedClass || 'Unassigned';
          finalMetadata.section = metadata.section || userData.lastSelectedSection || 'Unassigned';
        }
      }
    } catch (e) {
      console.warn('⚠️ Failed to fetch user data for mentor metadata:', e?.message || e);
    }
  }

  console.log('🔄 Final metadata being saved:', finalMetadata);

  // If we have an explicit activityIdForUpdate, SHORT‑CIRCUIT the lookup
  // and update that document directly. This is only used by Reimagine
  // Studio when feedback is generated and prevents any duplicate cards.
  if (activityIdForUpdate) {
    try {
      const docRef = doc(db, 'userActivities', activityIdForUpdate);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        console.warn('⚠️ activityIdForUpdate provided but document not found, falling back to normal lookup:', activityIdForUpdate);
      } else {
        const currentData = docSnap.data();
        const nextCount = (currentData.metadata?.transformationCount || 0) + 1;
        await updateDoc(docRef, {
          transformedImageUrl: finalTransformedImageUrl || currentData.transformedImageUrl || '',
          styleInfo: styleInfo || currentData.styleInfo || null,
          metadata: {
            ...(currentData.metadata || {}),
            ...finalMetadata,
            imageHash: finalMetadata.imageHash || currentData.metadata?.imageHash || null,
            transformationCount: nextCount
          },
          updatedAt: serverTimestamp()
        });
        console.log('✅ Updated reimagine activity via explicit activityIdForUpdate:', activityIdForUpdate);
        return activityIdForUpdate;
      }
    } catch (e) {
      console.warn('⚠️ Failed to update via activityIdForUpdate, falling back to normal lookup:', e?.message || e);
    }
  }

  // Deduplicate: update latest reimagine for this artwork instead of creating a new one
  try {
    const existing = await checkExistingActivity(originalImageUrl, ACTIVITY_TYPES.REIMAGINE);
    if (existing) {
      const nextCount = (existing.metadata?.transformationCount || 0) + 1;
      await updateDoc(doc(db, 'userActivities', existing.id), {
        // keep title/description stable
        transformedImageUrl: finalTransformedImageUrl || existing.transformedImageUrl || '',
        styleInfo: styleInfo || existing.styleInfo || null,
        metadata: {
          ...(existing.metadata || {}),
          ...finalMetadata,
          imageHash: finalMetadata.imageHash || existing.metadata?.imageHash || null,
          transformationCount: nextCount
        },
        updatedAt: serverTimestamp()
      });
      console.log('✅ Updated existing reimagine activity for artwork instead of creating new:', existing.id);
      return existing.id;
    }
  } catch (e) {
    console.warn('⚠️ Reimagine dedup/update failed, will create new activity:', e?.message || e);
  }

  return await saveActivity({
    type: ACTIVITY_TYPES.REIMAGINE,
    title,
    description,
    originalImageUrl,
    transformedImageUrl: finalTransformedImageUrl,
    styleInfo,
    metadata: { ...finalMetadata, transformationCount: finalMetadata.transformationCount || 1 }
  });
};

/**
 * Save Tutorial activity - Only when explicitly saved
 * @param {Object} params - Tutorial parameters
 * @param {boolean} isSaved - Whether the tutorial is being saved
 */
export const saveTutorialActivity = async (params, isSaved = false) => {
  const {
    originalImageUrl,
    transformedImageUrl,
    styleInfo,
    tutorial,
    title = 'Tutorial Generated',
    description = 'Step-by-step tutorial created',
    originalArtworkId
  } = params;

  // Only save to dashboard if explicitly saved
  if (!isSaved) {
    console.log('📝 Tutorial not saved yet, skipping dashboard save');
    return null;
  }

  return await saveActivity({
    type: ACTIVITY_TYPES.TUTORIAL,
    title,
    description,
    originalImageUrl,
    transformedImageUrl,
    styleInfo,
    tutorial,
    metadata: {
      feature: 'tutorial',
      styleName: styleInfo?.name || 'Unknown',
      stepsCount: tutorial?.steps?.length || 0,
      originalArtworkId: originalArtworkId
    }
  });
};

/**
 * Save Style Transformation activity (for art style transformations)
 * @param {Object} params - Style transformation parameters
 */
export const saveStyleTransformationActivity = async (params) => {
  const {
    originalImageUrl,
    transformedImageUrl,
    styleInfo,
    title = 'Style Transformation',
    description = 'Artwork transformed to new style'
  } = params;

  return await saveActivity({
    type: ACTIVITY_TYPES.REIMAGINE,
    title: `${styleInfo?.name || 'Style'} Transformation`,
    description: `Artwork transformed to ${styleInfo?.name || 'new'} style`,
    originalImageUrl,
    transformedImageUrl,
    styleInfo,
    metadata: {
      feature: 'style-transformation',
      styleName: styleInfo?.name || 'Unknown',
      styleId: styleInfo?.id || 'unknown'
    }
  });
};

/**
 * Check if an activity already exists for a given artwork and style
 * @param {string} originalImageUrl - Original artwork URL
 * @param {string} activityType - Type of activity to check (tutorial, reimagine, etc.)
 * @param {Object} styleInfo - Style information (optional, for style-specific activities)
 * @returns {Object|null} - Existing activity data or null if not found
 */
export const checkExistingActivity = async (originalImageUrlOrId, activityType, styleInfo = null, options = {}) => {
  const user = auth.currentUser;
  if (!user) {
    console.warn('No user logged in, cannot check existing activity');
    return null;
  }

  try {
    const matchBy = options.matchBy || 'originalImageUrl';

    console.log('🔍 checkExistingActivity called with:', {
      originalImageUrlOrId,
      activityType,
      styleInfo,
      userId: user.uid,
      matchBy
    });

    // Try to find the Firebase Storage URL equivalent (only when matching by URL)
    let firebaseStorageUrl = null;
    if (matchBy === 'originalImageUrl' && originalImageUrlOrId && typeof originalImageUrlOrId === 'string' && originalImageUrlOrId.includes('localhost:3001/uploads/')) {
      // Extract filename from local URL
      const filename = originalImageUrlOrId.split('/uploads/')[1];
      console.log('🔍 Extracted filename:', filename);
      
      // Try to find the corresponding Firebase Storage URL
      const activitiesQuery = query(
        collection(db, 'userActivities'),
        where('userId', '==', user.uid),
        where('type', '==', 'reimagine'),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(activitiesQuery);
      for (const doc of snapshot.docs) {
        const data = doc.data();
        if (data.originalImageUrl && data.originalImageUrl.includes('firebasestorage.googleapis.com')) {
          console.log('🔍 Found Firebase Storage URL:', data.originalImageUrl);
          firebaseStorageUrl = data.originalImageUrl;
          break;
        }
      }
    }

    // Always generate imageHash for consistent deduplication (more stable than URL matching)
    let imageHash = null;
    try {
      if (typeof originalImageUrlOrId === 'string' && originalImageUrlOrId.length > 0) {
        // Simple fast hash - generate for all URLs, not just large ones
        let h = 0;
        for (let i = 0; i < originalImageUrlOrId.length; i++) {
          h = (h * 31 + originalImageUrlOrId.charCodeAt(i)) >>> 0;
        }
        imageHash = h.toString(16);
        console.log('🔍 Generated imageHash:', imageHash, 'for key length:', originalImageUrlOrId.length);
      }
    } catch (e) {
      console.warn('⚠️ Failed to generate imageHash:', e?.message || e);
    }

    // Try imageHash first (more stable and avoids Firestore query limitations)
    let snapshot = null;
    
    if (imageHash) {
      try {
        console.log('🔍 Trying imageHash query first (recommended):', imageHash);
        let hashQuery;
        if (matchBy === 'originalArtworkId') {
          // When matching by artwork id, prefer direct match on metadata.originalArtworkId
          hashQuery = query(
            collection(db, 'userActivities'),
            where('userId', '==', user.uid),
            where('type', '==', activityType),
            where('metadata.originalArtworkId', '==', originalImageUrlOrId)
          );
        } else {
          hashQuery = query(
            collection(db, 'userActivities'),
            where('userId', '==', user.uid),
            where('type', '==', activityType),
            where('metadata.imageHash', '==', imageHash)
          );
        }
        
        if (styleInfo && styleInfo.id) {
          // Try with style filter, but if it fails (index error), fall back to without style filter
          try {
            hashQuery = query(
              collection(db, 'userActivities'),
              where('userId', '==', user.uid),
              where('type', '==', activityType),
              where('metadata.imageHash', '==', imageHash),
              where('styleInfo.id', '==', styleInfo.id)
            );
            snapshot = await getDocs(hashQuery);
            console.log('🔍 ImageHash query with style result:', snapshot.size, 'matches');
          } catch (styleError) {
            // If style query fails (likely index error), try without style filter
            console.warn('⚠️ ImageHash query with style failed, trying without style filter:', styleError?.message || styleError);
            hashQuery = query(
              collection(db, 'userActivities'),
              where('userId', '==', user.uid),
              where('type', '==', activityType),
              where('metadata.imageHash', '==', imageHash)
            );
            snapshot = await getDocs(hashQuery);
            console.log('🔍 ImageHash query without style result:', snapshot.size, 'matches');
            // Filter by style in memory if we got results
            if (!snapshot.empty && styleInfo && styleInfo.id) {
              const filteredDocs = snapshot.docs.filter(doc => {
                const data = doc.data();
                return data.styleInfo?.id === styleInfo.id;
              });
              // Create a new QuerySnapshot-like object with filtered docs
              snapshot = {
                ...snapshot,
                docs: filteredDocs,
                empty: filteredDocs.length === 0,
                size: filteredDocs.length
              };
              console.log('🔍 After in-memory style filter:', snapshot.size, 'matches');
            }
          }
        } else {
          snapshot = await getDocs(hashQuery);
          console.log('🔍 ImageHash query result:', snapshot.size, 'matches');
        }
      } catch (e) {
        // If it's an index error, log it but don't block - continue with fallback queries
        if (e?.code === 'failed-precondition' || e?.message?.includes('index')) {
          console.warn('⚠️ ImageHash query requires index, continuing with fallback:', e?.message || e);
        } else {
          console.warn('⚠️ ImageHash query failed:', e?.message || e);
        }
        snapshot = null;
      }
    }
    
    // Fallback to original key query only if imageHash/artworkId didn't work or wasn't available
    if (!snapshot || snapshot.empty) {
      try {
        console.log('🔍 Trying original key query as fallback...');
        let activitiesQuery;
        if (matchBy === 'originalArtworkId') {
          activitiesQuery = query(
            collection(db, 'userActivities'),
            where('userId', '==', user.uid),
            where('type', '==', activityType),
            where('metadata.originalArtworkId', '==', originalImageUrlOrId)
          );
        } else {
          activitiesQuery = query(
            collection(db, 'userActivities'),
            where('userId', '==', user.uid),
            where('type', '==', activityType),
            where('originalImageUrl', '==', originalImageUrlOrId)
          );
        }

        if (styleInfo && styleInfo.id) {
          console.log('🔍 Adding style filter for styleId:', styleInfo.id);
          activitiesQuery = query(
            collection(db, 'userActivities'),
            where('userId', '==', user.uid),
            where('type', '==', activityType),
            where(matchBy === 'originalArtworkId' ? 'metadata.originalArtworkId' : 'originalImageUrl', '==', originalImageUrlOrId),
            where('styleInfo.id', '==', styleInfo.id)
          );
        }

        snapshot = await getDocs(activitiesQuery);
        console.log('🔍 Original key query result:', snapshot.size, 'matches');
      } catch (e) {
        // If it's an index error, log it but don't block - return null to allow tutorial generation to proceed
        if (e?.code === 'failed-precondition' || e?.message?.includes('index')) {
          console.warn('⚠️ Original URL query requires index, allowing tutorial generation to proceed:', e?.message || e);
        } else {
          console.warn('⚠️ Original URL query failed (likely too large):', e?.message || e);
        }
        snapshot = null;
      }
    }
    
    // If not found and we have a Firebase Storage URL, try that
    if (snapshot && snapshot.empty && firebaseStorageUrl) {
      console.log('🔍 Trying Firebase Storage URL:', firebaseStorageUrl);
      
      let activitiesQuery = query(
        collection(db, 'userActivities'),
        where('userId', '==', user.uid),
        where('type', '==', activityType),
        where('originalImageUrl', '==', firebaseStorageUrl)
      );

      if (styleInfo && styleInfo.id) {
        activitiesQuery = query(
          collection(db, 'userActivities'),
          where('userId', '==', user.uid),
          where('type', '==', activityType),
          where('originalImageUrl', '==', firebaseStorageUrl),
          where('styleInfo.id', '==', styleInfo.id)
        );
      }
      
      snapshot = await getDocs(activitiesQuery);
    }
    
    console.log('🔍 Firestore query result:', {
      empty: snapshot.empty,
      size: snapshot.size,
      docs: snapshot.docs.map(doc => ({
        id: doc.id,
        data: doc.data()
      }))
    });
    
    if (!snapshot.empty) {
      // Pick the latest by createdAt in-memory to avoid requiring an index
      const latestDoc = snapshot.docs.reduce((latest, current) => {
        const latestTime = latest?.data()?.createdAt?.toDate ? latest.data().createdAt.toDate().getTime() : new Date(latest?.data()?.createdAt || 0).getTime();
        const currentTime = current?.data()?.createdAt?.toDate ? current.data().createdAt.toDate().getTime() : new Date(current?.data()?.createdAt || 0).getTime();
        return currentTime > latestTime ? current : latest;
      }, snapshot.docs[0]);
      const existingActivity = latestDoc.data();
      console.log('✅ Found existing activity:', {
        type: activityType,
        style: styleInfo?.name,
        createdAt: existingActivity.createdAt,
        id: latestDoc.id
      });
      return {
        id: latestDoc.id,
        ...existingActivity
      };
    }
    
      console.log('❌ No existing activity found for:', {
        type: activityType,
        style: styleInfo?.name,
        matchBy,
        key: originalImageUrlOrId,
        imageHash,
        firebaseStorageUrl: firebaseStorageUrl,
        userId: user.uid
      });
    return null;
  } catch (error) {
    // If it's an index error, log it but don't block - return null to allow tutorial generation to proceed
    if (error?.code === 'failed-precondition' || error?.message?.includes('index')) {
      console.warn('⚠️ checkExistingActivity requires index, allowing tutorial generation to proceed:', error?.message || error);
    } else {
      console.error('Error checking existing activity:', error);
    }
    return null; // Return null to allow tutorial generation to proceed even if check fails
  }
};

/**
 * Get all transformed artworks for a given original artwork
 * @param {string} originalArtworkUrl - URL of the original artwork
 * @param {string} userId - User ID
 */
export const getTransformedArtworksForOriginal = async (originalArtworkUrl, userId) => {
  try {
    console.log('🔍 Fetching transformed artworks for original:', originalArtworkUrl);
    
    // Query userActivities collection (for reimagine activities)
    const activitiesQuery = query(
      collection(db, 'userActivities'),
      where('userId', '==', userId),
      where('originalImageUrl', '==', originalArtworkUrl),
      where('type', '==', ACTIVITY_TYPES.REIMAGINE) // Only reimagine, not tutorial
    );
    
    // Also include tutorial activities saved in userActivities (type === 'tutorial')
    const userTutorialsQuery = query(
      collection(db, 'userActivities'),
      where('userId', '==', userId),
      where('originalImageUrl', '==', originalArtworkUrl),
      where('type', '==', ACTIVITY_TYPES.TUTORIAL)
    );
    
    // Query tutorialArtworks collection (for tutorial uploads)
    // Note: tutorialArtworks uses 'originalArtworkUrl' field
    // We'll query all user's tutorial artworks and filter client-side to handle URL variations
    const tutorialQuery = query(
      collection(db, 'tutorialArtworks'),
      where('userId', '==', userId)
    );
    
    console.log('🔍 Querying tutorialArtworks for userId:', userId, 'with originalArtworkUrl:', originalArtworkUrl?.substring(0, 100));
    
    // Execute both queries in parallel
    const [activitiesSnapshot, tutorialActivitiesSnapshot, tutorialSnapshot] = await Promise.all([
      getDocs(activitiesQuery),
      getDocs(userTutorialsQuery),
      getDocs(tutorialQuery)
    ]);
    
    // Process userActivities results (only reimagine activities)
    const activities = activitiesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Process tutorial activities stored in userActivities
    const userTutorialActivities = tutorialActivitiesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      // Normalize shape to ensure compatibility with consumer components
      type: ACTIVITY_TYPES.TUTORIAL,
      transformedImageUrl: doc.data().transformedImageUrl || doc.data().imageUrl || '',
      styleInfo: doc.data().styleInfo || { id: doc.data()?.metadata?.styleId, name: doc.data()?.metadata?.styleName }
    }));
    
    // Process tutorialArtworks results and format them to match userActivities structure
    // Filter client-side to handle URL variations (query params, encoding, etc.)
    const normalizeUrlForMatching = (url) => {
      if (!url) return '';
      try {
        const urlObj = new URL(url);
        return `${urlObj.origin}${urlObj.pathname}`;
      } catch {
        return url.split('?')[0].split('#')[0];
      }
    };
    
    const normalizedOriginalUrl = normalizeUrlForMatching(originalArtworkUrl);
    
    const tutorials = tutorialSnapshot.docs
      .filter(doc => {
        const data = doc.data();
        const artworkOriginalUrl = data.originalArtworkUrl || data.originalImageUrl || '';
        const normalizedArtworkUrl = normalizeUrlForMatching(artworkOriginalUrl);
        // Match if normalized URLs match OR exact URLs match (handles query params, encoding differences)
        const matches = normalizedArtworkUrl === normalizedOriginalUrl || artworkOriginalUrl === originalArtworkUrl;
        if (!matches) {
          console.log('🔍 Filtering out tutorialArtwork (URL mismatch):', {
            id: doc.id,
            style: data.styleName,
            expected: normalizedOriginalUrl?.substring(0, 80),
            got: normalizedArtworkUrl?.substring(0, 80)
          });
        } else {
          console.log('✅ Including tutorialArtwork (URL match):', {
            id: doc.id,
            style: data.styleName,
            url: normalizedArtworkUrl?.substring(0, 80)
          });
        }
        return matches;
      })
      .map(doc => {
        const data = doc.data();
        const artwork = {
          id: doc.id,
          type: 'tutorial',
          userId: data.userId,
          originalImageUrl: data.originalArtworkUrl || data.originalImageUrl, // Support both field names
          transformedImageUrl: data.imageUrl,
          styleInfo: {
            id: data.styleId,
            name: data.styleName
          },
          title: data.title,
          description: data.description,
          createdAt: data.createdAt,
          visibility: data.visibility,
          // Preserve original tutorial data
          tutorialData: {
            originalArtworkId: data.originalArtworkId,
            styleId: data.styleId,
            styleName: data.styleName,
            tutorialArtworkId: doc.id
          },
          // Include original fields for compatibility
          originalArtworkUrl: data.originalArtworkUrl || data.originalImageUrl,
          imageUrl: data.imageUrl,
          styleId: data.styleId,
          styleName: data.styleName
        };
        console.log('📦 Processed tutorialArtwork:', {
          id: artwork.id,
          originalImageUrl: artwork.originalImageUrl?.substring(0, 100),
          transformedImageUrl: artwork.transformedImageUrl?.substring(0, 100),
          styleName: artwork.styleName
        });
        return artwork;
      });
    
    console.log('📊 Tutorial artworks found:', {
      count: tutorials.length,
      artworks: tutorials.map(t => ({
        id: t.id,
        style: t.styleName,
        hasOriginalUrl: !!t.originalImageUrl,
        hasTransformedUrl: !!t.transformedImageUrl,
        originalUrl: t.originalImageUrl?.substring(0, 80),
        transformedUrl: t.transformedImageUrl?.substring(0, 80)
      }))
    });
    
    console.log('📊 All artworks before deduplication:', {
      activities: activities.length,
      userTutorialActivities: userTutorialActivities.length,
      tutorials: tutorials.length,
      total: activities.length + userTutorialActivities.length + tutorials.length
    });
    
    // Combine and deduplicate results
    const allTransformed = [...activities, ...userTutorialActivities, ...tutorials];
    
    // Deduplicate based on artwork ID - only remove true duplicates (same ID from different collections)
    // Each distinct artwork upload should be shown, even if they have the same style
    const deduplicated = allTransformed.reduce((acc, current) => {
      const artworkId = current.id || '';
      
      // If we have an artwork ID, use it as the unique key
      if (artworkId) {
        // Check if we've already seen this exact artwork ID
        if (!acc.seenIds.has(artworkId)) {
          acc.seenIds.add(artworkId);
          acc.results.push(current);
          console.log('✅ Added unique artwork by ID:', {
            id: artworkId,
            style: current.styleInfo?.name || current.styleName,
            type: current.type
          });
        } else {
          // Same ID found - check if we should replace it (prefer tutorialArtworks over userActivities)
          const existingIndex = acc.results.findIndex(r => r.id === artworkId);
          if (existingIndex >= 0) {
            const existing = acc.results[existingIndex];
            // If current is from tutorialArtworks and existing is from userActivities, replace it
            if (current.tutorialData?.tutorialArtworkId && !existing.tutorialData?.tutorialArtworkId) {
              console.log('🔄 Replacing userActivities tutorial with tutorialArtworks version:', {
                existingId: existing.id,
                newId: current.id,
                style: current.styleInfo?.name || current.styleName
              });
              acc.results[existingIndex] = current;
            } else {
              console.log('🔄 Skipping duplicate artwork (same ID):', {
                skippedId: current.id,
                keptId: existing.id,
                style: current.styleInfo?.name || current.styleName
              });
            }
          }
        }
      } else {
        // No artwork ID - use content-based deduplication as fallback
        const normalizeUrl = (url) => {
          if (!url) return '';
          try {
            const urlObj = new URL(url);
            return `${urlObj.origin}${urlObj.pathname}`;
          } catch {
            return url.split('?')[0].split('#')[0];
          }
        };
        
        const transformedUrl = normalizeUrl(current.transformedImageUrl || current.imageUrl || '');
        const originalUrl = normalizeUrl(current.originalImageUrl || current.originalArtworkUrl || '');
        const styleId = current.styleInfo?.id || current.styleId || '';
        const timestamp = current.createdAt?.toMillis?.() || current.createdAt?.seconds || current.timestamp || Date.now();
        const contentKey = `${current.userId}_${originalUrl}_${styleId}_${transformedUrl}_${timestamp}`;
        
        if (!acc.seenContent.has(contentKey)) {
          acc.seenContent.add(contentKey);
          acc.results.push(current);
          console.log('✅ Added unique artwork by content (no ID):', {
            style: current.styleInfo?.name || current.styleName,
            contentKey: contentKey.substring(0, 100)
          });
        } else {
          console.log('🔄 Skipping duplicate artwork (same content, no ID):', {
            style: current.styleInfo?.name || current.styleName
          });
        }
      }
      
      return acc;
    }, { seenIds: new Set(), seenContent: new Set(), results: [] });
    
    // Sort by creation date
    const sortedResults = deduplicated.results.sort((a, b) => {
      const aTime = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const bTime = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      return bTime - aTime;
    });
    
    console.log('🔄 Found transformed artworks:', {
      total: sortedResults.length,
      reimagineActivities: activities.length,
      userTutorialActivities: userTutorialActivities.length,
      tutorialsCollection: tutorials.length,
      duplicatesRemoved: allTransformed.length - sortedResults.length
    });
    
    return sortedResults;
  } catch (error) {
    console.error('❌ Error fetching transformed artworks:', error);
    return [];
  }
};

/**
 * Get transformed artworks for a given original artwork filtered by feature
 * @param {string} originalArtworkUrl - URL of the original artwork
 * @param {string} userId - User ID
 * @param {string} feature - Feature to filter by (e.g., 'reimagine-artwork', 'ways-of-seeing-journey')
 */
export const getTransformedArtworksByFeature = async (originalArtworkUrl, userId, feature) => {
  try {
    const activitiesQuery = query(
      collection(db, 'userActivities'),
      where('userId', '==', userId),
      where('originalImageUrl', '==', originalArtworkUrl),
      where('type', '==', ACTIVITY_TYPES.REIMAGINE),
      where('metadata.feature', '==', feature),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(activitiesQuery);
    const transformedArtworks = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`🔄 Found ${transformedArtworks.length} transformed artworks with feature '${feature}'`);
    return transformedArtworks;
  } catch (error) {
    console.error('❌ Error fetching transformed artworks by feature:', error);
    return [];
  }
};

/**
 * Test function to check Firestore access
 */
export const testFirestoreAccess = async () => {
  const user = auth.currentUser;
  console.log('🧪 Testing Firestore access:', {
    user: user ? { uid: user.uid, email: user.email } : null,
    isAuthenticated: !!user
  });
  
  if (!user) {
    console.error('❌ No user authenticated');
    return false;
  }
  
  try {
    // First, try to read from a collection that we know works (artworks)
    console.log('🧪 Testing artworks collection access...');
    const artworksQuery = query(
      collection(db, 'artworks'),
      where('uid', '==', user.uid),
      limit(1)
    );
    
    const artworksSnapshot = await getDocs(artworksQuery);
    console.log('✅ Artworks collection access successful:', {
      docsCount: artworksSnapshot.docs.length,
      empty: artworksSnapshot.empty
    });
    
    // Now try to read from userActivities collection
    console.log('🧪 Testing userActivities collection access...');
    const testQuery = query(
      collection(db, 'userActivities'),
      where('userId', '==', user.uid),
      limit(1)
    );
    
    const snapshot = await getDocs(testQuery);
    console.log('✅ userActivities read test successful:', {
      docsCount: snapshot.docs.length,
      empty: snapshot.empty
    });
    
    // Try to write a test document first (this might create the collection)
    console.log('🧪 Testing userActivities write access...');
    const testDoc = {
      userId: user.uid,
      type: 'test',
      title: 'Test Activity',
      description: 'Test activity for debugging',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    try {
      const docRef = await addDoc(collection(db, 'userActivities'), testDoc);
      console.log('✅ userActivities write test successful:', docRef.id);
      
      // Clean up - delete the test document
      await deleteDoc(doc(db, 'userActivities', docRef.id));
      console.log('✅ Test document cleaned up');
    } catch (writeError) {
      console.error('❌ userActivities write test failed:', writeError);
      console.error('❌ Write error details:', {
        code: writeError.code,
        message: writeError.message
      });
    }
    
    return true;
  } catch (error) {
    console.error('❌ Firestore read test failed:', error);
    console.error('❌ Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    return false;
  }
};

/**
 * Enhanced debugging function to check user authentication and Firestore permissions
 */
export const debugActivitySaving = async () => {
  const user = auth.currentUser;
  console.log('🔍 DEBUG: Activity Saving Diagnostics');
  console.log('🔍 User authentication status:', {
    isAuthenticated: !!user,
    uid: user?.uid,
    email: user?.email,
    emailVerified: user?.emailVerified
  });
  
  if (!user) {
    console.error('❌ DEBUG: No authenticated user found');
    return {
      success: false,
      error: 'No authenticated user',
      recommendations: ['Please sign in first', 'Check if auth state is properly initialized']
    };
  }
  
  try {
    // First, try to read existing activities to see if the collection exists
    console.log('🔍 DEBUG: Checking if userActivities collection exists...');
    try {
      const existingQuery = query(
        collection(db, 'userActivities'),
        where('userId', '==', user.uid),
        limit(1)
      );
      
      const existingSnapshot = await getDocs(existingQuery);
      console.log('✅ DEBUG: Collection exists and is readable:', {
        docsCount: existingSnapshot.docs.length,
        empty: existingSnapshot.empty
      });
    } catch (readError) {
      console.warn('⚠️ DEBUG: Could not read existing activities:', readError.message);
    }
    
    // Test basic Firestore access
    console.log('🔍 DEBUG: Testing basic Firestore access...');
    const testDoc = {
      userId: user.uid,
      type: 'debug-test',
      title: 'Debug Test Activity',
      description: 'Temporary test for debugging',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(db, 'userActivities'), testDoc);
    console.log('✅ DEBUG: Successfully created test activity:', docRef.id);
    
    // Try to read it back
    const readQuery = query(
      collection(db, 'userActivities'),
      where('userId', '==', user.uid),
      where('type', '==', 'debug-test')
    );
    
    const readSnapshot = await getDocs(readQuery);
    console.log('✅ DEBUG: Successfully read test activity:', {
      found: !readSnapshot.empty,
      count: readSnapshot.docs.length
    });
    
    // Clean up
    await deleteDoc(doc(db, 'userActivities', docRef.id));
    console.log('✅ DEBUG: Successfully cleaned up test activity');
    
    return {
      success: true,
      message: 'Activity saving is working correctly',
      userId: user.uid,
      testActivityId: docRef.id
    };
    
  } catch (error) {
    console.error('❌ DEBUG: Activity saving test failed:', error);
    console.error('❌ DEBUG: Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    
    // Provide specific recommendations based on error type
    let recommendations = [];
    
    if (error.code === 'permission-denied') {
      recommendations = [
        'Firestore rules may not be properly deployed - try refreshing the page',
        'User authentication may not be fully established - try signing out and back in',
        'The userActivities collection may not exist yet - this will be created on first write',
        'Check if Firebase project configuration is correct'
      ];
    } else if (error.code === 'unavailable') {
      recommendations = [
        'Firebase service may be temporarily unavailable',
        'Check your internet connection',
        'Try again in a few minutes'
      ];
    } else {
      recommendations = [
        'Check Firestore rules for userActivities collection',
        'Verify user authentication is properly set up',
        'Check if the userActivities collection exists',
        'Verify Firebase project configuration'
      ];
    }
    
    return {
      success: false,
      error: error.message,
      errorCode: error.code,
      recommendations
    };
  }
};

/**
 * Find existing Ways of Seeing journey for the same artwork + style (one pair → one journey doc).
 * Order: metadata.wosJourneyKey → imageHash + styleInfo.id → originalArtworkId + style → URL + style → legacy (no style).
 */
async function findExistingWaysOfSeeingJourneyDoc(user, originalImageUrl, originalArtworkId = null, styleId = null) {
  const run = async (label, q) => {
    try {
      const snap = await getDocs(q);
      if (!snap.empty) {
        const d = snap.docs[0];
        console.log('🎨 Found ways-of-seeing journey via', label, ':', d.id);
        return { id: d.id, ...d.data() };
      }
    } catch (e) {
      if (e?.code === 'failed-precondition' || String(e?.message || '').includes('index')) {
        console.warn('⚠️ Ways-of-seeing lookup needs index or failed:', label, e?.message || e);
      } else {
        console.warn('⚠️ Ways-of-seeing lookup failed:', label, e?.message || e);
      }
    }
    return null;
  };

  const styleKey = styleId != null && String(styleId).trim() !== '' ? String(styleId).trim() : null;
  const imageHash = computeImageIdentityHash(originalImageUrl);
  const wosKey = styleKey ? computeWaysOfSeeingJourneyKey(originalImageUrl, styleKey) : null;

  if (wosKey) {
    const byWos = await run('metadata.wosJourneyKey', query(
      collection(db, 'userActivities'),
      where('userId', '==', user.uid),
      where('metadata.feature', '==', 'ways-of-seeing-journey'),
      where('metadata.wosJourneyKey', '==', wosKey),
      orderBy('createdAt', 'desc'),
      limit(1)
    ));
    if (byWos) return byWos;
  }

  if (imageHash && styleKey) {
    const byHashStyle = await run('metadata.imageHash+styleInfo.id', query(
      collection(db, 'userActivities'),
      where('userId', '==', user.uid),
      where('metadata.feature', '==', 'ways-of-seeing-journey'),
      where('metadata.imageHash', '==', imageHash),
      where('styleInfo.id', '==', styleKey),
      orderBy('createdAt', 'desc'),
      limit(1)
    ));
    if (byHashStyle) return byHashStyle;
  }

  if (
    styleKey &&
    originalArtworkId &&
    typeof originalArtworkId === 'string' &&
    originalArtworkId.length > 0 &&
    !/^https?:\/\//i.test(originalArtworkId) &&
    !originalArtworkId.startsWith('data:')
  ) {
    const byArtId = await run('metadata.originalArtworkId+styleInfo.id', query(
      collection(db, 'userActivities'),
      where('userId', '==', user.uid),
      where('metadata.feature', '==', 'ways-of-seeing-journey'),
      where('metadata.originalArtworkId', '==', originalArtworkId),
      where('styleInfo.id', '==', styleKey),
      orderBy('createdAt', 'desc'),
      limit(1)
    ));
    if (byArtId) return byArtId;
  }

  if (styleKey) {
    const byUrlStyle = await run('originalImageUrl+styleInfo.id', query(
      collection(db, 'userActivities'),
      where('userId', '==', user.uid),
      where('metadata.feature', '==', 'ways-of-seeing-journey'),
      where('originalImageUrl', '==', originalImageUrl),
      where('styleInfo.id', '==', styleKey),
      orderBy('createdAt', 'desc'),
      limit(1)
    ));
    if (byUrlStyle) return byUrlStyle;
  }

  // Legacy docs without style scoping (avoid when styleKey is known — would merge different styles)
  if (!styleKey && imageHash) {
    const byHash = await run('metadata.imageHash (legacy)', query(
      collection(db, 'userActivities'),
      where('userId', '==', user.uid),
      where('metadata.feature', '==', 'ways-of-seeing-journey'),
      where('metadata.imageHash', '==', imageHash),
      orderBy('createdAt', 'desc'),
      limit(1)
    ));
    if (byHash) return byHash;
  }

  if (!styleKey && originalArtworkId && typeof originalArtworkId === 'string' && originalArtworkId.length > 0 &&
      !/^https?:\/\//i.test(originalArtworkId) && !originalArtworkId.startsWith('data:')) {
    const byArtOnly = await run('metadata.originalArtworkId (legacy)', query(
      collection(db, 'userActivities'),
      where('userId', '==', user.uid),
      where('metadata.feature', '==', 'ways-of-seeing-journey'),
      where('metadata.originalArtworkId', '==', originalArtworkId),
      orderBy('createdAt', 'desc'),
      limit(1)
    ));
    if (byArtOnly) return byArtOnly;
  }

  if (!styleKey) {
    return await run('originalImageUrl (legacy)', query(
      collection(db, 'userActivities'),
      where('userId', '==', user.uid),
      where('metadata.feature', '==', 'ways-of-seeing-journey'),
      where('originalImageUrl', '==', originalImageUrl),
      orderBy('createdAt', 'desc'),
      limit(1)
    ));
  }

  return null;
}

/**
 * Save Reimagine Journey - Auto-saves when prompt is submitted and tracks all transformations
 * @param {Object} params - Reimagine parameters
 * @param {boolean} isInitialPrompt - Whether this is the initial prompt submission
 */
export const saveWaysOfSeeingJourney = async (params, isInitialPrompt = false) => {
  console.log('🎨 saveWaysOfSeeingJourney called with params:', params);
  console.log('🎨 isInitialPrompt:', isInitialPrompt);
  
  const {
    originalImageUrl,
    transformedImageUrl,
    styleInfo,
    prompt,
    feedback,
    title = 'Artwork Reimagined',
    description = 'Artwork transformed with Artmosphere',
    originalArtworkId,
    journeyId = null // If provided, updates existing journey
  } = params;
  
  console.log('🎨 Extracted parameters:', {
    originalImageUrl,
    transformedImageUrl,
    styleInfo,
    prompt,
    feedback,
    title,
    description,
    originalArtworkId,
    journeyId
  });

  const user = auth.currentUser;
  console.log('🎨 Current user:', user ? { uid: user.uid, email: user.email } : null);
  
  if (!user) {
    console.warn('No user logged in, cannot save reimagine journey');
    return null;
  }

  try {
    const artworkIdentityHash = computeImageIdentityHash(originalImageUrl);

    // Match existing journey by stable hash / artwork id first (URLs can drift across hosts).
    let existingJourney = null;
    if (!journeyId) {
      existingJourney = await findExistingWaysOfSeeingJourneyDoc(user, originalImageUrl, originalArtworkId, styleInfo?.id);
    }

    let journeyDocRef = null;

    // Resolve row: explicit journeyId (session) or lookup by artwork+style
    let journeyRow = existingJourney;
    if (journeyId && !journeyRow) {
      const snap = await getDoc(doc(db, 'userActivities', journeyId));
      if (snap.exists()) {
        journeyRow = { id: snap.id, ...snap.data() };
      }
    }

    if (journeyRow) {
      // One Firestore doc per artwork+style: refresh URLs/metadata only (no duplicate journey steps).
      console.log('🎨 Refreshing ways-of-seeing journey:', journeyRow.id);
      journeyDocRef = doc(db, 'userActivities', journeyRow.id);

      const journeyData = journeyRow;

      const essentialStyleInfo = {};
      if (styleInfo?.id) essentialStyleInfo.id = styleInfo.id;
      if (styleInfo?.name) essentialStyleInfo.name = styleInfo.name;
      if (styleInfo?.description) essentialStyleInfo.description = styleInfo.description.substring(0, 200);
      if (styleInfo?.category) essentialStyleInfo.category = styleInfo.category;

      const wosJourneyKey = computeWaysOfSeeingJourneyKey(originalImageUrl, styleInfo?.id);

      const patch = {
        transformedImageUrl: transformedImageUrl,
        styleInfo: essentialStyleInfo,
        'metadata.styleName': styleInfo?.name || journeyData.metadata?.styleName || 'Unknown',
        ...(artworkIdentityHash && !journeyData.metadata?.imageHash
          ? { 'metadata.imageHash': artworkIdentityHash }
          : {}),
        ...(wosJourneyKey && !journeyData.metadata?.wosJourneyKey
          ? { 'metadata.wosJourneyKey': wosJourneyKey }
          : {}),
        ...(styleInfo?.id && !journeyData.metadata?.wosStyleId
          ? { 'metadata.wosStyleId': styleInfo.id }
          : {}),
        updatedAt: serverTimestamp(),
      };

      const jArr = journeyData.journey;
      if (Array.isArray(jArr) && jArr.length > 0) {
        const li = jArr.length - 1;
        patch[`journey.${li}.transformedImageUrl`] = transformedImageUrl;
        patch[`journey.${li}.prompt`] = prompt;
      }

      await updateDoc(journeyDocRef, patch);

      console.log('✅ Refreshed ways-of-seeing journey (no duplicate step):', journeyRow.id);
      return journeyRow.id;
    } else if (isInitialPrompt) {
      // Create new journey - only store essential style info to avoid document size limits
      console.log('🎨 Original styleInfo:', styleInfo);
      
      const essentialStyleInfo = {};
      
      // Only add fields that have values (not undefined/null)
      if (styleInfo?.id) essentialStyleInfo.id = styleInfo.id;
      if (styleInfo?.name) essentialStyleInfo.name = styleInfo.name;
      if (styleInfo?.description) essentialStyleInfo.description = styleInfo.description.substring(0, 200);
      if (styleInfo?.category) essentialStyleInfo.category = styleInfo.category;
      
      console.log('🎨 Essential styleInfo (filtered):', essentialStyleInfo);
      
      const imageHash = artworkIdentityHash;
      const wosJourneyKey = computeWaysOfSeeingJourneyKey(originalImageUrl, styleInfo?.id);
      if (imageHash) {
        console.log('🎨 Generated imageHash:', imageHash, 'for URL length:', originalImageUrl.length);
      }
      if (wosJourneyKey) {
        console.log('🎨 Ways of Seeing journey key (artwork+style):', wosJourneyKey);
      }
      
      // Build metadata with mentor context if applicable
      const metadata = {
        feature: 'ways-of-seeing-journey',
        styleName: styleInfo?.name || 'Unknown',
        originalArtworkId: originalArtworkId,
        isJourney: true,
        imageHash: imageHash,
      };
      if (wosJourneyKey) metadata.wosJourneyKey = wosJourneyKey;
      if (styleInfo?.id) metadata.wosStyleId = styleInfo.id;
      
      // Add mentor metadata if user is a mentor
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.accountType === 'mentor') {
            metadata.context = 'mentor';
            metadata.mentorId = user.uid;
            if (userData.mentorType) metadata.mentorType = userData.mentorType;
            // Get class/section from localStorage if available (for mentor uploads)
            try {
              if (typeof window !== 'undefined') {
                const mentorClass = localStorage.getItem('mentorClass');
                const mentorSection = localStorage.getItem('mentorSection');
                if (mentorClass) metadata.className = mentorClass;
                if (mentorSection) metadata.section = mentorSection;
                
                // Get artist name from localStorage if available
                const mentorSelectedArtwork = localStorage.getItem('mentorSelectedArtwork');
                if (mentorSelectedArtwork) {
                  try {
                    const parsed = JSON.parse(mentorSelectedArtwork);
                    if (parsed?.artist) metadata.artistName = parsed.artist;
                  } catch (_) {}
                }
              }
            } catch (e) {
              console.warn('⚠️ Failed to get mentor metadata from localStorage:', e?.message || e);
            }
          }
        }
      } catch (e) {
        console.warn('⚠️ Failed to fetch user data for mentor metadata:', e?.message || e);
      }
      
      const journey = {
        userId: user.uid,
        type: ACTIVITY_TYPES.REIMAGINE,
        title: title,
        description: description,
        originalImageUrl: originalImageUrl,
        originalArtworkId: originalArtworkId,
        transformedImageUrl: transformedImageUrl,
        styleInfo: essentialStyleInfo, // Use essential info only
        journey: [{
          step: 1,
          prompt: prompt,
          transformedImageUrl: transformedImageUrl,
          feedback: feedback || null,
          timestamp: new Date(),
          styleInfo: essentialStyleInfo // Use essential info only
        }],
        currentStep: 1,
        metadata: metadata,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      console.log('🎨 Creating journey document with data:', journey);
      
      // Check document size to prevent Firestore size limit errors
      const journeyString = JSON.stringify(journey);
      const sizeInBytes = new Blob([journeyString]).size;
      const sizeInMB = (sizeInBytes / (1024 * 1024)).toFixed(2);
      console.log(`🎨 Document size: ${sizeInBytes} bytes (${sizeInMB} MB)`);
      
      if (sizeInBytes > 1000000) { // 1MB limit
        console.warn('⚠️ Document size exceeds 1MB, this may cause Firestore errors');
      }
      
      console.log('🎨 About to save to Firebase collection: userActivities');
      journeyDocRef = await addDoc(collection(db, 'userActivities'), journey);
      console.log('✅ New ways of seeing journey created:', journeyDocRef.id);
    } else {
      console.warn('Cannot add to journey without journeyId or isInitialPrompt flag');
      return null;
    }

    return journeyDocRef.id;
  } catch (error) {
    console.error('❌ Error saving ways of seeing journey:', error);
    return null;
  }
};

/**
 * Latest Ways of Seeing journey document id for an original image URL (same matching as saveWaysOfSeeingJourney).
 * Used to attach tutorial data to the correct userActivities doc when only a URL was passed by mistake.
 */
export const findLatestWaysOfSeeingJourneyIdForArtwork = async (originalImageUrl, originalArtworkId = null, styleId = null) => {
  const user = auth.currentUser;
  if (!user || !originalImageUrl || typeof originalImageUrl !== 'string') return null;
  try {
    const docSnap = await findExistingWaysOfSeeingJourneyDoc(user, originalImageUrl.trim(), originalArtworkId, styleId);
    return docSnap?.id || null;
  } catch (e) {
    console.warn('findLatestWaysOfSeeingJourneyIdForArtwork failed:', e?.message || e);
    return null;
  }
};

/**
 * Add transformation step to existing reimagine journey
 * @param {string} journeyId - The journey document ID
 * @param {Object} transformationData - New transformation data
 */
export const addTransformationStep = async (journeyId, transformationData) => {
  const {
    prompt,
    transformedImageUrl,
    feedback,
    styleInfo
  } = transformationData;

  const user = auth.currentUser;
  if (!user) {
    console.warn('No user logged in, cannot add transformation step');
    return null;
  }

  try {
    const journeyRef = doc(db, 'userActivities', journeyId);
    
    // Get current journey to determine next step number
    const journeyDoc = await getDoc(journeyRef);
    if (!journeyDoc.exists()) {
      console.warn('Journey not found:', journeyId);
      return null;
    }

    const journeyData = journeyDoc.data();
    const existingStyleId = journeyData.styleInfo?.id || journeyData.metadata?.wosStyleId;
    const newStyleId = styleInfo?.id;
    const sameStyleRefresh =
      existingStyleId &&
      newStyleId &&
      String(existingStyleId) === String(newStyleId);

    // Only store essential style info to avoid document size limits
    const essentialStyleInfo = {};
    
    // Only add fields that have values (not undefined/null)
    if (styleInfo?.id) essentialStyleInfo.id = styleInfo.id;
    if (styleInfo?.name) essentialStyleInfo.name = styleInfo.name;
    if (styleInfo?.description) essentialStyleInfo.description = styleInfo.description.substring(0, 200);
    if (styleInfo?.category) essentialStyleInfo.category = styleInfo.category;

    if (sameStyleRefresh) {
      const patch = {
        transformedImageUrl,
        styleInfo: essentialStyleInfo,
        updatedAt: serverTimestamp(),
        'metadata.styleName': styleInfo?.name || journeyData.metadata?.styleName || 'Unknown',
      };
      const jArr = journeyData.journey;
      if (Array.isArray(jArr) && jArr.length > 0) {
        const li = jArr.length - 1;
        patch[`journey.${li}.transformedImageUrl`] = transformedImageUrl;
        patch[`journey.${li}.prompt`] = prompt;
      }
      await updateDoc(journeyRef, patch);
      console.log('✅ Refreshed same artwork+style journey (no new step):', journeyId);
      return journeyData.currentStep || 1;
    }

    const nextStep = (journeyData.currentStep || 0) + 1;
    const newStep = {
      step: nextStep,
      prompt: prompt,
      transformedImageUrl: transformedImageUrl,
      feedback: feedback || null,
      timestamp: new Date(),
      styleInfo: essentialStyleInfo // Use essential info only
    };

    // Update the journey with new step
    await updateDoc(journeyRef, {
      journey: arrayUnion(newStep),
      currentStep: nextStep,
      updatedAt: serverTimestamp(),
      // Update the main transformed image to the latest
      transformedImageUrl: transformedImageUrl
    });

    console.log('✅ Transformation step added to journey:', journeyId, 'step:', nextStep);
    return nextStep;
  } catch (error) {
    console.error('❌ Error adding transformation step:', error);
    return null;
  }
};

/**
 * Normalize artwork URL for matching/unlock keys
 * Strips query params and hashes to create a stable key.
 */
const normalizeArtworkUrlForKey = (url) => {
  if (!url || typeof url !== 'string') return '';
  try {
    const urlObj = new URL(url);
    return `${urlObj.origin}${urlObj.pathname}`;
  } catch {
    return url.split('?')[0].split('#')[0];
  }
};

/**
 * Generate deterministic document ID for Ways of Seeing unlock state
 * Based on userId + normalizedArtworkUrl
 */
const getUnlockDocId = (userId, originalArtworkUrl) => {
  const normalized = normalizeArtworkUrlForKey(originalArtworkUrl);
  const key = `${userId || 'anon'}::${normalized}`;
  // Simple hash to keep ID short and Firestore-safe
  let h = 0;
  for (let i = 0; i < key.length; i++) {
    h = (h * 31 + key.charCodeAt(i)) >>> 0;
  }
  return `unlock_${h.toString(16)}`;
};

/**
 * Get or create art style unlock state for a given artwork and user.
 * - Starts with 3 base styles unlocked by default.
 * - Stores state in 'waysOfSeeingUnlocks' collection.
 */
export const getOrCreateArtStyleUnlockState = async (originalArtworkUrl, userId) => {
  if (!originalArtworkUrl || !userId) {
    console.warn('[activityService] getOrCreateArtStyleUnlockState called without url/userId', {
      originalArtworkUrl,
      userId
    });
    return { unlockedStyleIds: [], pendingUnlockSlots: 0 };
  }

  const normalizedUrl = normalizeArtworkUrlForKey(originalArtworkUrl);
  const docId = getUnlockDocId(userId, normalizedUrl);
  const unlockRef = doc(db, 'waysOfSeeingUnlocks', docId);

  try {
    const unlockSnap = await getDoc(unlockRef);

    if (unlockSnap.exists()) {
      const data = unlockSnap.data();
      const existing = Array.isArray(data.unlockedStyleIds) ? data.unlockedStyleIds : [];
      const pendingSlots = typeof data.pendingUnlockSlots === 'number' ? data.pendingUnlockSlots : 0;

      return {
        id: docId,
        unlockedStyleIds: existing,
        pendingUnlockSlots: pendingSlots,
        originalArtworkUrl: data.originalArtworkUrl || normalizedUrl,
        userId: data.userId || userId
      };
    }

    // Create new unlock document - NO styles unlocked initially (user must choose first 3)
    const newDoc = {
      userId,
      originalArtworkUrl: normalizedUrl,
      unlockedStyleIds: [], // Start empty - user chooses first 3
      pendingUnlockSlots: 0, // No pending slots initially
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(unlockRef, newDoc);

    console.log('✅ Created new Ways of Seeing unlock state (empty - user must choose first 3):', {
      docId,
      userId,
      originalArtworkUrl: normalizedUrl,
      unlockedStyleIds: []
    });

    return {
      id: docId,
      unlockedStyleIds: [],
      pendingUnlockSlots: 0,
      originalArtworkUrl: normalizedUrl,
      userId
    };
  } catch (error) {
    console.error('❌ Failed to load/create art style unlock state:', error);
    return { unlockedStyleIds: [], pendingUnlockSlots: 0 };
  }
};

/**
 * Add unlocked art styles for a given artwork and user.
 * Also reduces pending unlock slots by the number of styles unlocked.
 */
export const addUnlockedArtStyles = async (originalArtworkUrl, userId, styleIds = []) => {
  if (!originalArtworkUrl || !userId || !Array.isArray(styleIds) || styleIds.length === 0) {
    return;
  }

  const normalizedUrl = normalizeArtworkUrlForKey(originalArtworkUrl);
  const docId = getUnlockDocId(userId, normalizedUrl);
  const unlockRef = doc(db, 'waysOfSeeingUnlocks', docId);

  try {
    // Ensure document exists first
    const currentState = await getOrCreateArtStyleUnlockState(originalArtworkUrl, userId);
    const currentPending = currentState.pendingUnlockSlots || 0;
    const slotsToConsume = Math.min(styleIds.length, currentPending);

    // Add unlocked styles and reduce pending slots
    await updateDoc(unlockRef, {
      unlockedStyleIds: arrayUnion(...styleIds),
      pendingUnlockSlots: Math.max(0, currentPending - slotsToConsume),
      updatedAt: serverTimestamp()
    });

    console.log('✅ Added unlocked art styles:', {
      userId,
      originalArtworkUrl: normalizedUrl,
      styleIds,
      consumedSlots: slotsToConsume,
      remainingPending: Math.max(0, currentPending - slotsToConsume)
    });
  } catch (error) {
    console.error('❌ Failed to add unlocked art styles:', error);
  }
};

/**
 * Add pending unlock slots (when user uploads tutorial but skips choosing styles)
 * @param {string} originalArtworkUrl - Original artwork URL
 * @param {string} userId - User ID
 * @param {number} slots - Number of slots to add (1 for private, 3 for shared)
 */
export const addPendingUnlockSlots = async (originalArtworkUrl, userId, slots = 0) => {
  if (!originalArtworkUrl || !userId || slots <= 0) {
    return;
  }

  const normalizedUrl = normalizeArtworkUrlForKey(originalArtworkUrl);
  const docId = getUnlockDocId(userId, normalizedUrl);
  const unlockRef = doc(db, 'waysOfSeeingUnlocks', docId);

  try {
    // Ensure document exists first
    const currentState = await getOrCreateArtStyleUnlockState(originalArtworkUrl, userId);
    const currentPending = currentState.pendingUnlockSlots || 0;

    await updateDoc(unlockRef, {
      pendingUnlockSlots: currentPending + slots,
      updatedAt: serverTimestamp()
    });

    console.log('✅ Added pending unlock slots:', {
      userId,
      originalArtworkUrl: normalizedUrl,
      slotsAdded: slots,
      totalPending: currentPending + slots
    });
  } catch (error) {
    console.error('❌ Failed to add pending unlock slots:', error);
  }
};


/**
 * Get reimagine journey by ID
 * @param {string} journeyId - The journey document ID
 */
export const getReimagineJourney = async (journeyId) => {
  const user = auth.currentUser;
  if (!user) {
    console.warn('No user logged in, cannot get reimagine journey');
    return null;
  }

  try {
    const journeyRef = doc(db, 'userActivities', journeyId);
    const journeyDoc = await getDoc(journeyRef);
    
    if (!journeyDoc.exists()) {
      console.warn('Journey not found:', journeyId);
      return null;
    }

    return {
      id: journeyDoc.id,
      ...journeyDoc.data()
    };
  } catch (error) {
    console.error('❌ Error getting reimagine journey:', error);
    return null;
  }
};

// Clean up existing tutorial activities (delete separate tutorial cards)
export const cleanupTutorialActivities = async () => {
  const user = auth.currentUser;
  if (!user) {
    console.warn('No user logged in, cannot cleanup tutorial activities');
    return;
  }

  try {
    console.log('🧹 Cleaning up separate tutorial activities...');
    
    const tutorialQuery = query(
      collection(db, 'userActivities'),
      where('userId', '==', user.uid),
      where('type', '==', 'tutorial')
    );

    const snapshot = await getDocs(tutorialQuery);
    
    console.log(`🗑️ Found ${snapshot.size} separate tutorial activities to delete`);
    
    const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
    
    console.log('✅ Cleaned up all separate tutorial activities');
  } catch (error) {
    console.error('❌ Error cleaning up tutorial activities:', error);
  }
};

// Generate unique tutorial ID based on style and artwork
const generateTutorialId = (styleId, artworkId) => {
  return `tutorial_${styleId}_${artworkId}`;
};

// Check if tutorial exists within reimagine activity using tutorial ID
export const checkTutorialById = async (styleId, artworkId) => {
  const user = auth.currentUser;
  if (!user) {
    console.warn('No user logged in, cannot check tutorial by ID');
    return null;
  }

  // Check cache first
  const cacheKey = createCacheKey('tutorial', { styleId, artworkId }, user.uid);
  const cached = getCache(cacheKey);
  if (cached !== null) {
    // Cache hit - null means no tutorial exists, object means tutorial exists
    console.log('✅ [TUTORIAL] Using cached tutorial check:', !!cached);
    return cached;
  }

  try {
    const tutorialId = generateTutorialId(styleId, artworkId);
    console.log('🔍 Checking tutorial data within reimagine activity for ID:', tutorialId);

    // First try to get the document directly if artworkId is a valid Firestore document ID
    try {
      const reimagineDocRef = doc(db, 'userActivities', artworkId);
      const reimagineDoc = await getDoc(reimagineDocRef);
      
      console.log('🔍 Reimagine activity document result:', {
        exists: reimagineDoc.exists(),
        artworkId: artworkId
      });
      
      if (reimagineDoc.exists()) {
        const reimagineData = reimagineDoc.data();
        
        // Verify this is a reimagine activity for the current user
        if (reimagineData.type === 'reimagine' && reimagineData.userId === user.uid) {
          // Check if the reimagine activity has tutorial data
          if (reimagineData.tutorialData) {
            // Check if tutorial is stored separately
            if (reimagineData.tutorialData.storedSeparately && reimagineData.tutorialData.tutorialRef) {
              // Fetch tutorial from separate collection
              try {
                const tutorialDocRef = doc(db, 'tutorials', reimagineData.tutorialData.tutorialRef);
                const tutorialDoc = await getDoc(tutorialDocRef);
                
                if (tutorialDoc.exists()) {
                  const tutorialDocData = tutorialDoc.data();
                  console.log('✅ Found tutorial data in separate collection:', {
                    tutorialId: tutorialId,
                    reimagineId: reimagineDoc.id,
                    tutorialRef: reimagineData.tutorialData.tutorialRef
                  });
                  return {
                    id: reimagineDoc.id,
                    tutorialId: reimagineData.tutorialData.tutorialId,
                    tutorial: tutorialDocData.tutorial,
                    styleInfo: reimagineData.tutorialData.styleInfo,
                    createdAt: tutorialDocData.createdAt
                  };
                }
              } catch (tutorialError) {
                console.error('❌ Error fetching tutorial from separate collection:', tutorialError);
              }
            } else if (reimagineData.tutorialData.tutorial) {
              // Tutorial stored directly in activity document
              console.log('✅ Found existing tutorial data within reimagine activity:', {
                tutorialId: tutorialId,
                reimagineId: reimagineDoc.id,
                createdAt: reimagineData.tutorialData.createdAt
              });
              return {
                id: reimagineDoc.id,
                tutorialId: reimagineData.tutorialData.tutorialId,
                tutorial: reimagineData.tutorialData.tutorial,
                styleInfo: reimagineData.tutorialData.styleInfo,
                createdAt: reimagineData.tutorialData.createdAt
              };
            }
          }
          
          console.log('❌ Reimagine activity found but no matching tutorial data');
          console.log('🔍 Available tutorial data:', reimagineData.tutorialData);
        } else {
          console.log('❌ Document found but not a valid reimagine activity for current user');
        }
      }
    } catch (docError) {
      console.warn('⚠️ Error accessing document directly, trying query approach...');
    }
      
    // Fallback: try query approach to find the reimagine activity
      const reimagineQuery = query(
        collection(db, 'userActivities'),
        where('userId', '==', user.uid),
      where('type', '==', 'reimagine'),
      where('styleInfo.id', '==', styleId)
      );

      const snapshot = await getDocs(reimagineQuery);
      
      console.log('🔍 Reimagine activity query result:', {
        empty: snapshot.empty,
        size: snapshot.size,
      artworkId: artworkId,
      styleId: styleId
    });
    
    // Find the activity that matches the artworkId in any of its fields
    const matchingDoc = snapshot.docs.find(doc => {
      const data = doc.data();
      
      // Handle artworkId that might have _step_1 suffix
      const baseArtworkId = artworkId.replace(/_step_\d+$/, '');
      
      console.log('🔍 Checking document for match:', {
        docId: doc.id,
        originalImageUrl: data.originalImageUrl,
        transformedImageUrl: data.transformedImageUrl,
        artworkId: artworkId,
        baseArtworkId: baseArtworkId,
        matchesOriginal: data.originalImageUrl === artworkId,
        matchesTransformed: data.transformedImageUrl === artworkId,
        matchesDocId: doc.id === artworkId,
        matchesBaseDocId: doc.id === baseArtworkId
      });
      
      return data.originalImageUrl === artworkId || 
             data.transformedImageUrl === artworkId ||
             doc.id === artworkId ||
             doc.id === baseArtworkId;
    });
      
      if (matchingDoc) {
        const reimagineData = matchingDoc.data();
        
        // Check if the reimagine activity has tutorial data
        if (reimagineData.tutorialData) {
          // Check if tutorial is stored separately
          if (reimagineData.tutorialData.storedSeparately && reimagineData.tutorialData.tutorialRef) {
            // Fetch tutorial from separate collection
            try {
              const tutorialDocRef = doc(db, 'tutorials', reimagineData.tutorialData.tutorialRef);
              const tutorialDoc = await getDoc(tutorialDocRef);
              
              if (tutorialDoc.exists()) {
                const tutorialDocData = tutorialDoc.data();
                console.log('✅ Found tutorial data in separate collection (fallback):', {
                  tutorialId: tutorialId,
                  reimagineId: matchingDoc.id,
                  tutorialRef: reimagineData.tutorialData.tutorialRef
                });
                const tutorialResult = {
                  id: matchingDoc.id,
                  tutorialId: reimagineData.tutorialData.tutorialId,
                  tutorial: tutorialDocData.tutorial,
                  styleInfo: reimagineData.tutorialData.styleInfo,
                  createdAt: tutorialDocData.createdAt
                };
                // Cache the result (5 min cache)
                setCache(cacheKey, tutorialResult, 5 * 60 * 1000);
                console.log('✅ [TUTORIAL] Tutorial cached (fallback)');
                return tutorialResult;
              }
            } catch (tutorialError) {
              console.error('❌ Error fetching tutorial from separate collection:', tutorialError);
            }
          } else if (reimagineData.tutorialData.tutorial) {
            // Tutorial stored directly in activity document
            console.log('✅ Found existing tutorial data within reimagine activity (fallback):', {
              tutorialId: tutorialId,
              reimagineId: matchingDoc.id,
              createdAt: reimagineData.tutorialData.createdAt
            });
            const tutorialResult = {
              id: matchingDoc.id,
              tutorialId: reimagineData.tutorialData.tutorialId,
              tutorial: reimagineData.tutorialData.tutorial,
              styleInfo: reimagineData.tutorialData.styleInfo,
              createdAt: reimagineData.tutorialData.createdAt
            };
            // Cache the result (5 min cache)
            setCache(cacheKey, tutorialResult, 5 * 60 * 1000);
            console.log('✅ [TUTORIAL] Tutorial cached (fallback)');
            return tutorialResult;
          }
        }
        
        console.log('❌ Reimagine activity found but no matching tutorial data (fallback)');
    } else {
      console.log('❌ No matching reimagine activity found for artworkId:', artworkId);
    }
    
    console.log('❌ No existing tutorial found for ID:', tutorialId);
    // Cache null result (5 min cache) - means no tutorial exists
    setCache(cacheKey, null, 5 * 60 * 1000);
    console.log('✅ [TUTORIAL] No tutorial result cached');
    return null;
  } catch (error) {
    console.error('Error checking tutorial by ID:', error);
    return null;
  }
};

// Save tutorial data within the existing reimagine activity (no separate tutorial card)
export const saveTutorialWithId = async (tutorialData) => {
  const user = auth.currentUser;
  if (!user) {
    console.warn('No user logged in, cannot save tutorial');
    return null;
  }

  try {
    const tutorialId = generateTutorialId(tutorialData.styleInfo.id, tutorialData.artworkId);
    console.log('💾 activityService: Saving tutorial data within reimagine activity with ID:', tutorialId);
    console.log('💾 activityService: Tutorial data details:', {
      artworkId: tutorialData.artworkId,
      styleId: tutorialData.styleInfo.id,
      styleName: tutorialData.styleInfo.name,
      hasTutorial: !!tutorialData.tutorial,
      userId: user.uid
    });

    // Find the existing reimagine activity and update it with tutorial data
    // First try to get the document directly if artworkId is a valid Firestore document ID
    try {
      const reimagineDocRef = doc(db, 'userActivities', tutorialData.artworkId);
      const reimagineDoc = await getDoc(reimagineDocRef);
      
      console.log('💾 activityService: Document lookup result:', {
        exists: reimagineDoc.exists(),
        artworkId: tutorialData.artworkId
      });
      
      if (reimagineDoc.exists()) {
        const reimagineData = reimagineDoc.data();
        
        console.log('💾 activityService: Reimagine data details:', {
          type: reimagineData.type,
          userId: reimagineData.userId,
          currentUserId: user.uid,
          hasTutorialData: !!reimagineData.tutorialData
        });
        
        // Verify this is a reimagine activity for the current user
        if (reimagineData.type === 'reimagine' && reimagineData.userId === user.uid) {
          console.log('💾 activityService: Updating document with tutorial data...');
          
          // Check document size before updating
          const currentDataString = JSON.stringify(reimagineData);
          const currentSize = new Blob([currentDataString]).size;
          
          const tutorialDataToAdd = {
            tutorialId: tutorialId,
            tutorial: tutorialData.tutorial,
            styleInfo: tutorialData.styleInfo,
            createdAt: serverTimestamp()
          };
          const tutorialDataString = JSON.stringify(tutorialDataToAdd);
          const tutorialSize = new Blob([tutorialDataString]).size;
          
          const estimatedNewSize = currentSize + tutorialSize;
          const maxSize = 1000000; // 1MB limit
          
          console.log('💾 Document size check:', {
            currentSize: currentSize,
            tutorialSize: tutorialSize,
            estimatedNewSize: estimatedNewSize,
            willExceed: estimatedNewSize > maxSize
          });
          
          // If document would exceed size limit, store tutorial in separate collection
          if (estimatedNewSize > maxSize) {
            console.warn('⚠️ Document would exceed size limit, storing tutorial in separate collection');
            
            try {
              const tutorialDocRef = await addDoc(collection(db, 'tutorials'), {
                userId: user.uid,
                reimagineActivityId: reimagineDoc.id,
                tutorialId: tutorialId,
                tutorial: tutorialData.tutorial,
                styleInfo: tutorialData.styleInfo,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
              });
              
              await updateDoc(reimagineDocRef, {
                tutorialData: {
                  tutorialId: tutorialId,
                  tutorialRef: tutorialDocRef.id,
                  styleInfo: tutorialData.styleInfo,
                  createdAt: serverTimestamp(),
                  storedSeparately: true
                },
                updatedAt: serverTimestamp()
              });
              
              console.log('✅ Tutorial data saved in separate collection:', tutorialDocRef.id);
              return reimagineDoc.id;
            } catch (separateError) {
              console.error('❌ Error saving tutorial to separate collection:', separateError);
            }
          }
          
          // Try to update normally
          try {
            await updateDoc(reimagineDocRef, {
              tutorialData: {
                tutorialId: tutorialId,
                tutorial: tutorialData.tutorial,
                styleInfo: tutorialData.styleInfo,
                createdAt: serverTimestamp()
              },
              updatedAt: serverTimestamp()
            });
            
            console.log('✅ activityService: Tutorial data saved within reimagine activity:', reimagineDoc.id);
            return reimagineDoc.id;
          } catch (updateError) {
            // If update fails due to size, try storing in separate collection
            if (updateError.code === 'invalid-argument' && updateError.message?.includes('size')) {
              console.warn('⚠️ Update failed due to size, storing tutorial in separate collection');
              
              try {
                const tutorialDocRef = await addDoc(collection(db, 'tutorials'), {
                  userId: user.uid,
                  reimagineActivityId: reimagineDoc.id,
                  tutorialId: tutorialId,
                  tutorial: tutorialData.tutorial,
                  styleInfo: tutorialData.styleInfo,
                  createdAt: serverTimestamp(),
                  updatedAt: serverTimestamp()
                });
                
                await updateDoc(reimagineDocRef, {
                  tutorialData: {
                    tutorialId: tutorialId,
                    tutorialRef: tutorialDocRef.id,
                    styleInfo: tutorialData.styleInfo,
                    createdAt: serverTimestamp(),
                    storedSeparately: true
                  },
                  updatedAt: serverTimestamp()
                });
                
                console.log('✅ Tutorial data saved in separate collection (fallback):', tutorialDocRef.id);
                return reimagineDoc.id;
              } catch (separateError) {
                console.error('❌ Error saving tutorial to separate collection:', separateError);
                throw updateError;
              }
            } else {
              throw updateError;
            }
          }
        } else {
          console.warn('⚠️ activityService: Document found but not a valid reimagine activity for current user');
        }
      } else {
        console.warn('⚠️ activityService: No reimagine activity found for artworkId:', tutorialData.artworkId);
      }
    } catch (docError) {
      console.warn('⚠️ Error accessing document directly, trying query approach...');
    }
    
    // Fallback: try query approach to find the reimagine activity
    try {
      
      // Fallback: try query approach
      const reimagineQuery = query(
        collection(db, 'userActivities'),
        where('userId', '==', user.uid),
        where('type', '==', 'reimagine'),
        where('styleInfo.id', '==', tutorialData.styleInfo.id)
      );

      const snapshot = await getDocs(reimagineQuery);
      
      console.log('💾 activityService: Query result:', {
        empty: snapshot.empty,
        size: snapshot.size,
        artworkId: tutorialData.artworkId
      });
      
      // Find the activity that matches the artworkId in any of its fields
      const matchingDoc = snapshot.docs.find(doc => {
        const data = doc.data();
        
        // Handle artworkId that might have _step_1 suffix
        const baseArtworkId = tutorialData.artworkId.replace(/_step_\d+$/, '');
        
        console.log('💾 Checking document for match in save:', {
          docId: doc.id,
          originalImageUrl: data.originalImageUrl,
          transformedImageUrl: data.transformedImageUrl,
          artworkId: tutorialData.artworkId,
          baseArtworkId: baseArtworkId,
          matchesOriginal: data.originalImageUrl === tutorialData.artworkId,
          matchesTransformed: data.transformedImageUrl === tutorialData.artworkId,
          matchesDocId: doc.id === tutorialData.artworkId,
          matchesBaseDocId: doc.id === baseArtworkId
        });
        
        return data.originalImageUrl === tutorialData.artworkId || 
               data.transformedImageUrl === tutorialData.artworkId ||
               doc.id === tutorialData.artworkId ||
               doc.id === baseArtworkId;
      });
      
      if (matchingDoc) {
        // Check document size before updating
        const currentData = matchingDoc.data();
        const currentDataString = JSON.stringify(currentData);
        const currentSize = new Blob([currentDataString]).size;
        
        // Calculate size of tutorial data to be added
        const tutorialDataToAdd = {
          tutorialId: tutorialId,
          tutorial: tutorialData.tutorial,
          styleInfo: tutorialData.styleInfo,
          createdAt: serverTimestamp()
        };
        const tutorialDataString = JSON.stringify(tutorialDataToAdd);
        const tutorialSize = new Blob([tutorialDataString]).size;
        
        const estimatedNewSize = currentSize + tutorialSize;
        const maxSize = 1000000; // 1MB limit (with some buffer)
        
        console.log('💾 Document size check:', {
          currentSize: currentSize,
          tutorialSize: tutorialSize,
          estimatedNewSize: estimatedNewSize,
          maxSize: maxSize,
          willExceed: estimatedNewSize > maxSize
        });
        
        // If document would exceed size limit, store tutorial in separate collection
        if (estimatedNewSize > maxSize) {
          console.warn('⚠️ Document would exceed size limit, storing tutorial in separate collection');
          
          try {
            // Store tutorial in separate collection
            const tutorialDocRef = await addDoc(collection(db, 'tutorials'), {
              userId: user.uid,
              reimagineActivityId: matchingDoc.id,
              tutorialId: tutorialId,
              tutorial: tutorialData.tutorial,
              styleInfo: tutorialData.styleInfo,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
            
            // Store only reference in the activity document
            await updateDoc(matchingDoc.ref, {
              tutorialData: {
                tutorialId: tutorialId,
                tutorialRef: tutorialDocRef.id, // Reference to separate document
                styleInfo: tutorialData.styleInfo,
                createdAt: serverTimestamp(),
                storedSeparately: true // Flag to indicate it's in separate collection
              },
              updatedAt: serverTimestamp()
            });
            
            console.log('✅ Tutorial data saved in separate collection:', tutorialDocRef.id);
            return matchingDoc.id;
          } catch (separateError) {
            console.error('❌ Error saving tutorial to separate collection:', separateError);
            // Fall back to trying to update anyway (might work if size estimate was off)
          }
        }
        
        // Try to update normally if size is OK
        try {
          await updateDoc(matchingDoc.ref, {
            tutorialData: {
              tutorialId: tutorialId,
              tutorial: tutorialData.tutorial,
              styleInfo: tutorialData.styleInfo,
              createdAt: serverTimestamp()
            },
            updatedAt: serverTimestamp()
          });
          
          console.log('✅ Tutorial data saved within reimagine activity (fallback):', matchingDoc.id);
          return matchingDoc.id;
        } catch (updateError) {
          // If update fails due to size, try storing in separate collection
          if (updateError.code === 'invalid-argument' && updateError.message?.includes('size')) {
            console.warn('⚠️ Update failed due to size, storing tutorial in separate collection');
            
            try {
              const tutorialDocRef = await addDoc(collection(db, 'tutorials'), {
                userId: user.uid,
                reimagineActivityId: matchingDoc.id,
                tutorialId: tutorialId,
                tutorial: tutorialData.tutorial,
                styleInfo: tutorialData.styleInfo,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
              });
              
              await updateDoc(matchingDoc.ref, {
                tutorialData: {
                  tutorialId: tutorialId,
                  tutorialRef: tutorialDocRef.id,
                  styleInfo: tutorialData.styleInfo,
                  createdAt: serverTimestamp(),
                  storedSeparately: true
                },
                updatedAt: serverTimestamp()
              });
              
              console.log('✅ Tutorial data saved in separate collection (fallback):', tutorialDocRef.id);
              return matchingDoc.id;
            } catch (separateError) {
              console.error('❌ Error saving tutorial to separate collection:', separateError);
              throw updateError; // Re-throw original error
            }
          } else {
            throw updateError;
          }
        }
      } else {
        console.warn('⚠️ No reimagine activity found for artworkId:', tutorialData.artworkId);
        return null;
      }
    } catch (queryError) {
      console.error('❌ Error with query approach:', queryError);
      return null;
    }
  } catch (error) {
    console.error('❌ Error saving tutorial data:', error);
    return null;
  }
};

/**
 * Save Daily Activity with completion status and feedback
 * @param {Object} params - Daily activity parameters
 */
// Track ongoing saves to prevent race conditions
const ongoingSaves = new Map();

export const saveDailyActivity = async (params) => {
  const {
    activityId,
    activityTitle,
    activityDescription,
    userArtworkUrl,
    activityPageUrl,
    feedback = null,
    isCompleted = false,
    completedAt = null,
    metadata = {}
  } = params;

  const user = auth.currentUser;
  
  if (!user) {
    console.warn('No user logged in, cannot save daily activity');
    return null;
  }

  // Normalize activityId to number for consistency
  const normalizedActivityId = Number(activityId) || activityId;
  const saveKey = `${user.uid}_${normalizedActivityId}`;
  
  // Check if there's already an ongoing save for this activity
  if (ongoingSaves.has(saveKey)) {
    console.log('⏳ Waiting for ongoing save to complete for activity:', normalizedActivityId);
    try {
      // Wait for the existing save to complete
      await ongoingSaves.get(saveKey);
      // After it completes, check again and update if needed
      const existing = await checkDailyActivityExists(normalizedActivityId);
      if (existing) {
        // Update the existing one with any new data
        return await saveDailyActivity(params); // Recursive call, but now existing will be found
      }
    } catch (error) {
      console.error('❌ Error waiting for ongoing save:', error);
    }
  }

  // Create a promise for this save operation
  const savePromise = (async () => {
    try {
      // Check if activity already exists
      const existing = await checkDailyActivityExists(normalizedActivityId);
      
      console.log('🔍 Checking for existing activity:', {
        activityId: normalizedActivityId,
        found: !!existing,
        existingId: existing?.id
      });
    
    const activityData = {
      userId: user.uid,
      type: 'daily_activity',
      activityId: normalizedActivityId, // Always use normalized ID
      title: activityTitle || `Daily Activity ${normalizedActivityId}`,
      description: activityDescription || 'Daily creative activity for developing artistic thinking',
      updatedAt: serverTimestamp()
    };
    
    // Update fields conditionally
    if (userArtworkUrl) {
      activityData.transformedImageUrl = userArtworkUrl;
    }
    if (activityPageUrl) {
      activityData.originalImageUrl = activityPageUrl;
    }
    if (feedback !== null) {
      activityData.feedback = feedback;
    }
    if (isCompleted !== undefined) {
      activityData.isCompleted = isCompleted;
    }
    if (completedAt) {
      activityData.completedAt = completedAt;
    } else if (isCompleted && !existing?.completedAt) {
      activityData.completedAt = serverTimestamp();
    }
    
    // Merge metadata
    activityData.metadata = {
      ...(existing?.metadata || {}),
      ...metadata,
      activityType: 'daily_creative',
      prerequisites: metadata.prerequisites || existing?.metadata?.prerequisites || 'Art supplies and imagination',
      steps: metadata.steps || existing?.metadata?.steps || 'Follow the activity instructions and create your artwork'
    };
    
      if (existing) {
        // Update existing activity
        console.log('📝 Updating existing daily activity:', {
          docId: existing.id,
          activityId: normalizedActivityId,
          isCompleted: activityData.isCompleted,
          hasFeedback: !!activityData.feedback
        });
        
        await updateDoc(doc(db, 'userActivities', existing.id), activityData);
        console.log('✅ Daily activity updated successfully:', existing.id);
        
        // Clear cache to force refresh
        const user = auth.currentUser;
        if (user) {
          const cacheKey = createCacheKey('userActivities', { userId: user.uid }, user.uid);
          clearCache(cacheKey);
          console.log('✅ [ACTIVITY_SERVICE] Cache cleared after daily activity update');
        }
        
        return existing.id;
      } else {
        // Create new activity
        activityData.createdAt = serverTimestamp();
        if (!activityData.completedAt && isCompleted) {
          activityData.completedAt = serverTimestamp();
        }
        
        console.log('📝 Creating new daily activity:', {
          userId: activityData.userId,
          activityId: normalizedActivityId,
          isCompleted: activityData.isCompleted,
          hasFeedback: !!activityData.feedback
        });

        const docRef = await addDoc(collection(db, 'userActivities'), activityData);
        console.log('✅ Daily activity created successfully:', docRef.id);
        
        // Clear cache to force refresh (saveActivity already clears, but ensure it's cleared here too)
        const user = auth.currentUser;
        if (user) {
          const cacheKey = createCacheKey('userActivities', { userId: user.uid }, user.uid);
          clearCache(cacheKey);
          console.log('✅ [ACTIVITY_SERVICE] Cache cleared after daily activity create');
        }
        
        return docRef.id;
      }
    } catch (error) {
      console.error('❌ Error saving daily activity:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message
      });
      throw error; // Re-throw so the promise rejection is handled
    } finally {
      // Remove from ongoing saves map
      ongoingSaves.delete(saveKey);
    }
  })();
  
  // Store the promise
  ongoingSaves.set(saveKey, savePromise);
  
  try {
    const result = await savePromise;
    return result;
  } catch (error) {
    console.error('❌ Error in save promise:', error);
    return null;
  }
};

/**
 * Check if daily activity exists and get its data
 * @param {string} activityId - The activity ID to check
 * @returns {Object|null} - Activity data if exists, null otherwise
 */
export const checkDailyActivityExists = async (activityId) => {
  const user = auth.currentUser;
  
  if (!user) {
    return null;
  }

  try {
    // Normalize activityId to number for consistent comparison
    const normalizedActivityId = Number(activityId);
    
    // Query for activities with this activityId (handle both string and number)
    const q = query(
      collection(db, 'userActivities'),
      where('userId', '==', user.uid),
      where('type', '==', 'daily_activity')
    );
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      // Find activities matching this activityId (handle both string and number)
      const matchingDocs = querySnapshot.docs.filter(doc => {
        const data = doc.data();
        const docActivityId = Number(data.activityId) || data.activityId;
        return Number(docActivityId) === normalizedActivityId || String(docActivityId) === String(activityId);
      });
      
      if (matchingDocs.length > 0) {
        // If multiple matches, get the most recent one (by createdAt)
        const sortedDocs = matchingDocs.sort((a, b) => {
          const aTime = a.data().createdAt?.toMillis?.() || a.data().createdAt || 0;
          const bTime = b.data().createdAt?.toMillis?.() || b.data().createdAt || 0;
          return bTime - aTime; // Most recent first
        });
        
        const mostRecent = sortedDocs[0];
        
        // If there are multiple duplicates, log a warning
        if (matchingDocs.length > 1) {
          console.warn(`⚠️ Found ${matchingDocs.length} duplicate activities for activityId ${activityId}, using most recent:`, mostRecent.id);
        }
        
        return {
          id: mostRecent.id,
          ...mostRecent.data()
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('❌ Error checking daily activity:', error);
    return null;
  }
};

/**
 * Update daily activity with feedback
 * @param {string} activityDocId - The document ID of the activity
 * @param {Object} feedback - The feedback data
 */
export const updateDailyActivityFeedback = async (activityDocId, feedback) => {
  const user = auth.currentUser;
  
  if (!user) {
    console.warn('No user logged in, cannot update daily activity feedback');
    return false;
  }

  try {
    const activityRef = doc(db, 'userActivities', activityDocId);
    await updateDoc(activityRef, {
      feedback: feedback,
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ Daily activity feedback updated successfully');
    return true;
  } catch (error) {
    console.error('❌ Error updating daily activity feedback:', error);
    return false;
  }
};

/**
 * Save Art History Appreciation activity
 * @param {Object} params - Art history appreciation parameters
 */
export const saveArtHistoryAppreciationActivity = async (params) => {
  const {
    originalImageUrl,
    artwork,
    appreciation,
    points,
    suggestion,
    techniques,
    artAnalysis,
    concepts = [],
    activity = null,
    techniqueImprove = [],
    title = 'Art History Appreciation',
    description = 'Art history appreciation analysis',
    metadata = {}
  } = params;

  const user = auth.currentUser;
  if (!user) {
    console.warn('No user logged in, cannot save art history appreciation activity');
    return null;
  }

  try {
    // Generate imageHash for consistent deduplication
    let imageHash = null;
    try {
      if (typeof originalImageUrl === 'string' && originalImageUrl.length > 0) {
        let h = 0;
        for (let i = 0; i < originalImageUrl.length; i++) {
          h = (h * 31 + originalImageUrl.charCodeAt(i)) >>> 0;
        }
        imageHash = h.toString(16);
      }
    } catch (e) {
      console.warn('⚠️ Failed to generate imageHash:', e?.message || e);
    }

    // Check for existing activity
    const existing = await checkExistingActivity(originalImageUrl, ACTIVITY_TYPES.ART_HISTORY_APPRECIATION);
    if (existing) {
      try {
        await updateDoc(doc(db, 'userActivities', existing.id), {
          appreciation: appreciation || existing.appreciation || [],
          points: points || existing.points || [],
          suggestion: suggestion ?? existing.suggestion ?? '',
          techniques: techniques || existing.techniques || [],
          artAnalysis: artAnalysis || existing.artAnalysis || null,
          concepts: concepts || existing.concepts || [],
          activity: activity || existing.activity || null,
          techniqueImprove: techniqueImprove || existing.techniqueImprove || [],
          metadata: {
            ...(existing.metadata || {}),
            ...metadata,
            feature: 'art-history-appreciation',
            imageHash: imageHash || existing.metadata?.imageHash || null,
            artworkMeta: artwork ? {
              id: artwork.id,
              title: artwork.title,
              artist: artwork.artistName,
              movement: artwork.artMovement,
              typeOfArtwork: artwork.typeOfArtwork || artwork.typeOfArtworkLabel || '',
              levelType: artwork.levelType || artwork.level || ''
            } : existing.metadata?.artworkMeta
          },
          updatedAt: serverTimestamp()
        });
        return existing.id;
      } catch (e) {
        console.warn('⚠️ Failed to update existing art history appreciation, will create new one:', e?.message || e);
      }
    }

    const activityDocId = await saveActivity({
      type: ACTIVITY_TYPES.ART_HISTORY_APPRECIATION,
      title: title || `Art History: ${artwork?.title || 'Unknown Artwork'}`,
      description: description || `Art appreciation analysis for ${artwork?.title || 'artwork'}`,
      originalImageUrl,
      aiPoints: points || [],
      suggestion,
      techniques,
      artAnalysis,
      concepts,
      metadata: {
        feature: 'art-history-appreciation',
        imageHash: imageHash || null,
        artworkMeta: artwork ? {
          id: artwork.id,
          title: artwork.title,
          artist: artwork.artistName,
          movement: artwork.artMovement,
          typeOfArtwork: artwork.typeOfArtwork || artwork.typeOfArtworkLabel || '',
          levelType: artwork.levelType || artwork.level || ''
        } : null,
        ...metadata
      }
    });

    // Also save the activity and techniqueImprove if provided
    if (activity || techniqueImprove.length > 0) {
      try {
        await updateDoc(doc(db, 'userActivities', activityDocId), {
          activity: activity || null,
          techniqueImprove: techniqueImprove || [],
          appreciation: appreciation || []
        });
      } catch (e) {
        console.warn('⚠️ Failed to update activity/techniqueImprove:', e?.message || e);
      }
    }

    // Clear cache to force refresh (user's own activities)
    const cacheKey = createCacheKey('userActivities', { userId: user.uid }, user.uid);
    clearCache(cacheKey);
    console.log('✅ [ACTIVITY_SERVICE] Cache cleared after saveArtHistoryAppreciationActivity');
    
    return activityDocId;
  } catch (error) {
    console.error('❌ Error saving art history appreciation activity:', error);
    return null;
  }
};

/**
 * Save Art Comparison activity
 * @param {Object} params - Art comparison parameters
 */
export const saveArtComparisonActivity = async (params) => {
  const {
    artwork1,
    artwork2,
    comparisonResult,
    title = 'Art Comparison',
    description = 'Comparison between two artworks',
    metadata = {}
  } = params;

  const user = auth.currentUser;
  if (!user) {
    console.warn('No user logged in, cannot save art comparison activity');
    return null;
  }

  try {
    // Generate a unique key for this comparison (based on both artworks)
    const comparisonKey = `${artwork1?.id || artwork1?.imageUrl || ''}_${artwork2?.id || artwork2?.imageUrl || ''}`;
    let comparisonHash = null;
    try {
      if (comparisonKey.length > 0) {
        let h = 0;
        for (let i = 0; i < comparisonKey.length; i++) {
          h = (h * 31 + comparisonKey.charCodeAt(i)) >>> 0;
        }
        comparisonHash = h.toString(16);
      }
    } catch (e) {
      console.warn('⚠️ Failed to generate comparisonHash:', e?.message || e);
    }

    // Check for existing comparison activity for this artwork pair
    // Use artwork1's imageUrl as the key (same as grouping logic)
    const artwork1ImageUrl = artwork1?.imageUrl || '';
    const existing = await checkExistingActivity(artwork1ImageUrl, ACTIVITY_TYPES.ART_COMPARISON);
    
    if (existing) {
      // Check if this is the same comparison (same artwork pair)
      const existingArtwork1 = existing.metadata?.artwork1 || existing.artwork1 || {};
      const existingArtwork2 = existing.metadata?.artwork2 || existing.artwork2 || {};
      const existingArtwork1Id = existingArtwork1.id || existingArtwork1.imageUrl || '';
      const existingArtwork2Id = existingArtwork2.id || existingArtwork2.imageUrl || '';
      const newArtwork1Id = artwork1?.id || artwork1?.imageUrl || '';
      const newArtwork2Id = artwork2?.id || artwork2?.imageUrl || '';
      
      // If it's the same artwork pair, update the existing activity
      if (existingArtwork1Id === newArtwork1Id && existingArtwork2Id === newArtwork2Id) {
        try {
          // Store comparisons as an array to support multiple comparisons for the same pair
          const existingComparisons = existing.metadata?.comparisons || [];
          const newComparison = {
            comparisonResult: comparisonResult || null,
            createdAt: new Date().toISOString()
          };
          
          // Check if this exact comparison already exists (avoid duplicates)
          const isDuplicate = existingComparisons.some(comp => {
            const existingSimilarities = comp.comparisonResult?.similarities || [];
            const existingDifferences = comp.comparisonResult?.differences || [];
            const newSimilarities = comparisonResult?.similarities || [];
            const newDifferences = comparisonResult?.differences || [];
            
            // Simple check: if similarities and differences match, it's a duplicate
            return JSON.stringify(existingSimilarities) === JSON.stringify(newSimilarities) &&
                   JSON.stringify(existingDifferences) === JSON.stringify(newDifferences);
          });
          
          if (!isDuplicate) {
            existingComparisons.push(newComparison);
          }
          
          await updateDoc(doc(db, 'userActivities', existing.id), {
            artwork1: artwork1 || existing.artwork1 || null,
            artwork2: artwork2 || existing.artwork2 || null,
            comparisonResult: comparisonResult || existing.comparisonResult || null, // Keep latest as primary
            metadata: {
              ...(existing.metadata || {}),
              feature: 'art-comparison',
              comparisonHash: comparisonHash || existing.metadata?.comparisonHash || null,
              artwork1: artwork1 ? {
                id: artwork1.id,
                title: artwork1.title,
                artist: artwork1.artistName,
                movement: artwork1.artMovement,
                imageUrl: artwork1.imageUrl
              } : existing.metadata?.artwork1,
              artwork2: artwork2 ? {
                id: artwork2.id,
                title: artwork2.title,
                artist: artwork2.artistName,
                movement: artwork2.artMovement,
                imageUrl: artwork2.imageUrl
              } : existing.metadata?.artwork2,
              comparisons: existingComparisons, // Store all comparisons as array
              comparisonResult: comparisonResult ? {
                similarities: comparisonResult.similarities || [],
                differences: comparisonResult.differences || [],
                insight: comparisonResult.insight || '',
                activity: comparisonResult.activity || null
              } : existing.metadata?.comparisonResult,
              ...metadata
            },
            updatedAt: serverTimestamp()
          });
          console.log('✅ Updated existing art comparison activity:', existing.id);
          return existing.id;
        } catch (e) {
          console.warn('⚠️ Failed to update existing art comparison, will create new one:', e?.message || e);
        }
      }
    }

    const activityDocId = await saveActivity({
      type: ACTIVITY_TYPES.ART_COMPARISON,
      title: title || `Comparing: ${artwork1?.title || 'Artwork 1'} & ${artwork2?.title || 'Artwork 2'}`,
      description: description || `Comparison between ${artwork1?.title || 'artwork 1'} and ${artwork2?.title || 'artwork 2'}`,
      originalImageUrl: artwork1?.imageUrl || '',
      transformedImageUrl: artwork2?.imageUrl || '', // Use transformedImageUrl for artwork2
      // Store full comparison data at top level for easy access
      artwork1: artwork1 || null,
      artwork2: artwork2 || null,
      comparisonResult: comparisonResult || null,
      metadata: {
        feature: 'art-comparison',
        comparisonHash: comparisonHash || null,
        artwork1: artwork1 ? {
          id: artwork1.id,
          title: artwork1.title,
          artist: artwork1.artistName,
          movement: artwork1.artMovement,
          imageUrl: artwork1.imageUrl
        } : null,
        artwork2: artwork2 ? {
          id: artwork2.id,
          title: artwork2.title,
          artist: artwork2.artistName,
          movement: artwork2.artMovement,
          imageUrl: artwork2.imageUrl
        } : null,
        comparisonResult: comparisonResult ? {
          similarities: comparisonResult.similarities || [],
          differences: comparisonResult.differences || [],
          insight: comparisonResult.insight || '',
          activity: comparisonResult.activity || null
        } : null,
        comparisons: [{
          comparisonResult: comparisonResult || null,
          createdAt: new Date().toISOString()
        }], // Initialize with first comparison
        ...metadata
      }
    });

    return activityDocId;
  } catch (error) {
    console.error('❌ Error saving art comparison activity:', error);
    return null;
  }
};
